'use client';
/**
 * LE RÉFÉRENTIEL DES ANALYSES — l'écran où le catalogue se paramètre.
 *
 * L'application « manuel » (catalog.bio24.tn) n'est qu'une vitrine : tout ce
 * qui décrit un examen — ses paramètres, techniques, intervalles, critères,
 * délais, textes patient — se règle ici, et le circuit éditorial le publie.
 *
 * ── Ce que l'écran contient (état au 18/10/2026) ───────────────────────────
 *   · EXAMENS — la liste du catalogue, avec l'état du circuit par ligne ; une
 *     fiche s'ouvre en PAGE (`PageExamen`), à son adresse
 *     `/manuel-prelevement/<CODE>`, en quatre volets ;
 *   · PARAMÈTRES, TECHNIQUES, INTERPRÉTATION, CIRCUIT, PARAMÉTRAGE ;
 *   · la barre « Registres du référentiel » mène aux critères d'acceptation,
 *     aux intervalles de référence et aux critères de performance.
 *
 * ── Les deux routes d'écriture ──────────────────────────────────────────────
 *   · `ged/manuel-ref/*` — les RÉGLAGES du référentiel (composition, délai en
 *     heures, jours, nature, visibilité patients…), appliqués en direct ;
 *   · `ged/manuel-circuit/*` — les champs du CATALOGUE, au brouillon de la
 *     fiche, puis correction simple diffusée par le RQ seul, ou double
 *     relecture si un champ opposable est touché (`circuit-opposable.ts`).
 * La frontière est la liste `CHAMPS_DIRECTS` ci-dessous, alignée sur
 * `ACCEPTES` au moteur.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { OngletInterpretations } from '@/components/screens/manuel/Interpretations';
import { BoutonJournal } from '@/components/BoutonJournal';
import { useRemonteEnHaut,
  card, TitrePage, EmptyState, Skeleton, ChampRecherche,
  Modale, inputStyle, BoutonCreer, I, SelecteurPastilles, TitreRubrique, Pastille } from '@/components/ui';
import { OngletsDossier, PanneauDossier } from '@/components/Dossier';
import { FIELD_CATALOG } from '@/shared/catalogueChamps';
import { formaterDelai, formaterJours } from '@/shared/delaiRendu';
import { Tableau } from '@/shared/Tableau';
import { useGed } from '@/lib/store';
import {
  moteur, OngletTechniques, OngletIntervalles, OngletCriteresFiche,
  OngletTechniquesRegistre, OngletCqi,
} from './Registres';
import type { FicheDetail } from './Registres';
import { PerformanceFiche } from './PerformanceFiche';
import { PrelevementReception } from './PrelevementReception';
import { PreparationPatient } from './PreparationPatient';
import { OngletInterpretation } from './InterpretationFiche';
import { libelleComposition, marqueVersion, enRetard } from '@/shared/versionsComposition';
import { OngletParametres, Rubrique, libelleIntervalle, type LigneRubrique, type Parametre, type ChampVoc, type UniteVoc } from './ReferentielParametres';
import { REGLES_CODE, verifierCode, normaliserCode, motTronque } from '@/shared/codesReferentiel';
import { useUrlsModules } from '@/shared/urlsModulesClient';
import { useOngletAdresse } from '@/lib/useAdresse';
import { OngletCircuit } from './Circuit';
import { BandeauCircuit, ValeurAvecBrouillon, lireBrouillon, lireChampsOpposables, PASTILLE_CIRCUIT, type BrouillonFiche, type StatutCircuit } from './CircuitFiche';
import { OngletParametrage } from './Parametrage';
import { CataloguePlat } from './CataloguePlat';
import { RegistresLies } from './RegistresLies';
/* UNE règle de recherche, testée, partagée par les quatre endroits qui la
   recopiaient chacun à sa façon — dont aucun ne regardait les SYNONYMES
   (incident « glucose ne trouve pas glycémie », 26/08/2026). */
import { correspond, pertinence } from '@/lib/recherche-referentiel';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { Segment } from '@/shared/Segment';
import { PiedActions } from '@/shared/PiedActions';
type AnalyteRef = { documentId: string; code: string; nom: string; version?: string | null };
type LigneCatalogue = {
  documentId: string; code: string; libelle: string; famille: string | null;
  milieux: string[] | null; lieu: string | null; archivee: boolean; version?: string | null;
  affichageTechniques: 'fusion' | 'par-technique';
  delaiRenduHeures: number | null; joursRealisation: string[] | null; delaiRendu: string | null;
  /** Exigences préanalytiques structurées ; seule `surRendezVous` a un réglage à l'écran (20/10/2026). */
  preanalytique?: { surRendezVous?: boolean } & Record<string, unknown> | null;
  analytes: AnalyteRef[]; nbTechniques: number; nbIntervallesEnVigueur: number; nbCriteres: number;
  /** La nature (07/09/2026) : primaire, calcule, panel_fixe, panel_regles, panel_carte. */
  nature?: string;
};
const JOURS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'] as const;

const chip: React.CSSProperties = {
  display: 'inline-block', padding: '1px 7px', borderRadius: 'var(--radius-md)',
  background: 'var(--cyan-50)', color: 'var(--cyan-700)',
  fontSize: 12.5, fontWeight: 600, marginRight: 4,
};
const chipVide: React.CSSProperties = { ...chip, background: 'var(--amber-50)', color: 'var(--amber-600)' };

/* QUATRE VOLETS au lieu de dix onglets (Ines, 17/10/2026 : « il y a trop de
   choses et on ne sait pas où chercher »). Prélèvement — ce qu'on prélève et
   ce qu'on accepte ; Réalisation & rendu — identité, délais, tarifs,
   accréditation, réglages de structure ; Patients & diffusion — la liste
   courte, les textes patient, la fiche publique telle qu'elle se lit ; Qualité
   — CQI, performance, techniques, intervalles, critères, interprétation,
   regroupés derrière un seul volet parce qu'ils servent le technicien, pas la
   mise à jour du catalogue. */
type OngletFiche = 'prelevement' | 'realisation' | 'patients' | 'qualite';
/* Les valeurs ADMISES dans l'adresse — une adresse tronquée par un logiciel de
   messagerie ne doit pas ouvrir un onglet vide. */
const ONGLETS_FICHE: readonly OngletFiche[] = ['prelevement', 'realisation', 'patients', 'qualite'];
type VoletQualite = 'cqi' | 'performance' | 'techniques' | 'intervalles' | 'criteres' | 'interpretation';

/* ── La fiche d'un examen : une PAGE, et l'unique éditeur ─────────────────── */

/**
 * La fiche d'un examen (Ines, 17-18/10/2026).
 *
 * ── Une page, plus une fenêtre ──────────────────────────────────────────────
 * La fiche s'ouvrait dans une fenêtre de 1040 px par-dessus la liste. Elle a
 * une adresse propre depuis le 17/10 (`/manuel-prelevement/GLY`) : elle
 * devient une page, qu'on envoie à une collègue et qu'on met en favori. Le
 * retour à la liste conserve filtres et défilement, qui vivent dans l'écran
 * parent.
 *
 * ── Un seul éditeur : la fiche elle-même ────────────────────────────────────
 * Trois éditeurs écrivaient les mêmes champs : le Dossier (clic sur la
 * valeur), l'onglet « Avant / après » (deux colonnes, un enregistrement) et le
 * bouton « Modifier » du Circuit (un formulaire de trente lignes). Il n'en
 * reste qu'un : on clique une valeur, on la modifie sur place, on enregistre.
 * L'« Avant / après » n'est plus un onglet mais un ÉTAT de la fiche : dès
 * qu'un brouillon existe, un bandeau le dit en tête et chaque valeur en
 * attente s'affiche surlignée, l'ancienne barrée à côté. Les actes du circuit
 * — proposer, approuver, refuser, diffuser — sont dans ce bandeau.
 */
function PageExamen({ fiche, analytes, estRq, catalogue, onFerme, onEcrit }: {
  fiche: LigneCatalogue; analytes: AnalyteRef[]; estRq: boolean;
  /** Le catalogue entier — les COMPOSANTES d'un résultat calculé s'y choisissent. */
  catalogue: LigneCatalogue[];
  onFerme: () => void;
  /** Après toute écriture : la liste recharge ses lignes (compteurs, pastille). */
  onEcrit: () => void;
}) {
  /* Les cartes de contrôle interne viennent du MAGASIN, déjà chargées par le
     rafraîchissement général : les redemander au moteur pour un seul volet
     ajouterait un aller-retour à chaque ouverture d'analyse, pour une donnée
     que l'écran a déjà en main. */
  const { cartesCqi, analytes: refParametres, session } = useGed();
  const moi = session.name || session.key;
  const { manuel: MANUEL_URL } = useUrlsModules();
  const parametresLies = React.useMemo(
    () => fiche.analytes.map((a) => ({
      ...a,
      equipementCode: refParametres.find((r) => r.code === a.code)?.equipementCode ?? null,
    })),
    [fiche.analytes, refParametres],
  );
  /* Le volet vit dans l'ADRESSE (relevé P0.8) : on envoie « le prélèvement de
     GLY », pas « l'examen GLY, va voir le premier volet ». Le premier volet est
     le prélèvement pour tout le monde (RQ, 14/09/2026 : « on commence par le
     milieu qu'on peut prélever »). */
  const [ongletFiche, setOngletFiche] = useOngletAdresse<OngletFiche>('prelevement', ONGLETS_FICHE);
  const [sousQualite, setSousQualite] = useState<VoletQualite>('cqi');
  const [detail, setDetail] = useState<FicheDetail | null>(null);
  /* Les DVM citant l'examen ou l'un de ses paramètres — rattachées par CODE
     (`analyteCode`), la DVM ne portant pas de relation. Servies par la même
     lecture que le détail. */
  const [dvms, setDvms] = useState<{ ref: string; version: number | null; analyteCode: string; equipementCode: string | null; portee: string | null; statut: string | null; docCode: string | null }[]>([]);
  const [versionDetail, setVersionDetail] = useState(0);
  useEffect(() => {
    let vivant = true;
    void moteur(`manuel-ref/fiche/${encodeURIComponent(fiche.code)}`).then((r) => {
      if (!vivant || !r.ok) return;
      /* Les intervalles des PARAMÈTRES rejoignent ceux de l'examen, marqués de
         leur origine. Fusionnés ICI plutôt que servis à part : l'écran les
         regroupe déjà par paramètre, et deux listes séparées obligeraient à
         comparer deux tableaux pour savoir ce qui s'applique à un âge donné —
         exactement ce qu'on cherche à éviter. */
      const f = (r.data.fiche as unknown as FicheDetail) || null;
      const herites = (r.data.intervallesParametres as FicheDetail['intervalles']) || [];
      setDetail(f ? { ...f, intervalles: [...f.intervalles, ...herites] } : null);
      setDvms((r.data.dvms as typeof dvms) || []);
    });
    return () => { vivant = false; };
  }, [fiche.code, versionDetail]);
  const rechargerDetail = () => { setDetail(null); setVersionDetail((v) => v + 1); };

  /* LE BROUILLON DE CET EXAMEN — l'état de la fiche. Demandé au circuit à
     l'ouverture et après chaque écriture (25/08/2026 : un indicateur qui ne
     vivait que dans l'état de l'écran mourait au rechargement, et l'on
     modifiait deux fois le même champ sans voir la première). `undefined` =
     pas encore lu ; `null` = rien en attente. */
  const [brouillon, setBrouillon] = useState<BrouillonFiche | null | undefined>(undefined);
  /* La RÈGLE des deux vitesses — les champs opposables — vient du moteur avec
     le brouillon, jamais recopiée ici (leçon du 17/10 : deux listes, deux
     vérités). L'éditeur la lit pour dire, avant d'enregistrer, qui relira. */
  const [opposables, setOpposables] = useState<string[]>([]);
  const [versionBrouillon, setVersionBrouillon] = useState(0);
  useEffect(() => {
    let vivant = true;
    void moteur(`manuel-circuit/brouillon/${encodeURIComponent(fiche.code)}`).then((r) => {
      if (!vivant) return;
      setBrouillon(r.ok ? lireBrouillon(r.data) : null);
      if (r.ok) setOpposables(lireChampsOpposables(r.data));
    });
    return () => { vivant = false; };
  }, [fiche.code, versionBrouillon]);
  const rechargerBrouillon = () => setVersionBrouillon((v) => v + 1);
  /* Après toute écriture, les trois lectures repartent : le détail (la fiche
     publiée), le brouillon (ce qui attend) et la liste (compteurs, pastille). */
  const apresEcriture = () => { rechargerDetail(); rechargerBrouillon(); onEcrit(); };

  /* ── LES LISTES DÉROULANTES DU DOSSIER (RQ, 26/08/2026) ────────────────────
   * « Lorsque je souhaite modifier, me proposer les listes déroulantes quand il
   * y en a une. » Chargées UNE fois pour la page : le Dossier et le volet
   * Patients s'en servent tous deux. */
  const [listes, setListes] = useState<ListesDuDossier>(null);
  useEffect(() => {
    let vivant = true;
    void moteur('manuel-ref/parametrage').then((r) => {
      if (!vivant || !r.ok) return;
      setListes({
        lists: ((r.data.parametrage as Record<string, unknown> | null)?.lists as Record<string, string[]>) || {},
        enums: (r.data.enums as Record<string, { code: string; libelle: string }[]>) || {},
      });
    });
    return () => { vivant = false; };
  }, []);

  const [lies, setLies] = useState<string[]>(fiche.analytes.map((a) => a.documentId));
  const [delai, setDelai] = useState<string>(fiche.delaiRenduHeures === null ? '' : String(fiche.delaiRenduHeures));
  const [jours, setJours] = useState<string[]>(fiche.joursRealisation || []);
  const [surRdv, setSurRdv] = useState<boolean>(!!fiche.preanalytique?.surRendezVous);
  /* HEURE DE PRÉLÈVEMENT (31/10/2026) — « le matin », obligatoire ou de
     préférence, et sa plage. Le site oriente le créneau du patient, et
     l'équipe la retrouve sur la demande de rendez-vous. */
  const lirePre = (k: string) => (fiche.preanalytique as Record<string, unknown> | null | undefined)?.[k];
  const heureTexte = (v: unknown) => (typeof v === 'number' ? String(v).replace('.', ',') : '');
  const [matin, setMatin] = useState<string>(['obligatoire', 'preference'].includes(String(lirePre('matin'))) ? String(lirePre('matin')) : '');
  const [matinDe, setMatinDe] = useState<string>(heureTexte(lirePre('matinDe')));
  const [matinA, setMatinA] = useState<string>(heureTexte(lirePre('matinA')));
  /* `affichageTechniques` n'a plus de réglage à l'écran (18/08) : sur 315
     fiches, AUCUNE n'était en « par-technique » — un choix de mise en page du
     livret, offert depuis le référentiel, que personne n'a jamais employé. La
     valeur est REPOSTÉE telle quelle à l'enregistrement : retirer le champ de
     l'écran ne doit pas l'effacer en base. */
  const affichage = fiche.affichageTechniques;
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [reglagesFaits, setReglagesFaits] = useState('');

  // LA NATURE de l'examen (07/09/2026) et ce qui va avec — formule et
  // composantes d'un calculé, répertoire et jeu de règles d'un panel à règles.
  // Portés par le DÉTAIL : les états s'amorcent à sa première lecture. Déclarés
  // ICI, avec les autres : ce composant a des retours anticipés plus bas, et un
  // hook posé après eux fait tomber toute l'application (incident du 17/08).
  const [nature, setNature] = useState<string>('primaire');
  const [jeuDeRegles, setJeuDeRegles] = useState('');
  const [repertoire, setRepertoire] = useState<string[]>([]);
  const [parametres, setParametres] = useState<{ documentId: string; code: string; nom: string }[]>([]);
  const [formule, setFormule] = useState('');
  const [composantes, setComposantes] = useState<string[]>([]);
  const [calculAmorce, setCalculAmorce] = useState(false);
  /* ⚠️ AMORÇAGE AU RENDU, plus dans un effet (02/09/2026). Ces cinq états sont
     la copie éditable de ce que le serveur a rendu : les poser dans un effet
     faisait afficher le formulaire une passe VIDE — cases décochées, formule
     absente — avant de le remplir. Sur une fiche de paramètre calculé, cette
     passe montre « non calculé » d'un paramètre qui l'est.
     `calculAmorce` garde son rôle : l'amorçage n'a lieu QU'UNE FOIS, sans quoi
     un rechargement du détail écraserait une saisie en cours. */
  if (detail && !calculAmorce) {
    const d = detail as unknown as { nature?: string | null; jeuDeRegles?: string | null; formule?: string | null; composantes?: { documentId: string }[]; repertoire?: { documentId: string }[] };
    setNature(d.nature || 'primaire');
    setJeuDeRegles(d.jeuDeRegles || '');
    setRepertoire((d.repertoire || []).map((r) => r.documentId));
    setFormule(d.formule || '');
    setComposantes((d.composantes || []).map((c) => c.documentId));
    setCalculAmorce(true);
  }

  useEffect(() => {
    let vivant = true;
    /* ⚠️ 100 PARAMÈTRES SUR 330 — la liste était tronquée en silence.
       RQ, 25/08/2026 : « les listes ne sont pas complètes ». `pageSize=100`
       était écrit à la main, et deux cent trente paramètres restaient hors du
       sélecteur : rattacher l'un d'eux à un examen était simplement
       impossible, sans que rien ne le dise. C'est le plafond de pagination du
       mémo des pièges, atteint par une valeur codée en dur.

       On pagine donc jusqu'au bout plutôt que de deviner une borne : le jour
       où le catalogue passera 500 paramètres, rien ne se retronquera. */
    void (async () => {
      const out: { documentId: string; code: string; nom: string }[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const res = await fetch(
          `/api/ged/ged-analytes?pagination[page]=${page}&pagination[pageSize]=100&sort=code:asc&fields[0]=code&fields[1]=nom`,
          { credentials: 'include' },
        ).catch(() => null);
        if (!res || !res.ok) break;
        const d = await res.json().catch(() => null);
        const lot = ((d?.data as Record<string, unknown>[]) || []);
        out.push(...lot.map((a) => ({ documentId: String(a.documentId), code: String(a.code), nom: String(a.nom) })));
        if (lot.length < 100) break;
      }
      if (vivant) setParametres(out);
    })();
    return () => { vivant = false; };
  }, []);

  /* « Enregistrer les réglages » ne s'active que si un réglage a CHANGÉ
     (relevé du 29/10/2026 : bouton actif alors que rien n'était modifié — on
     ne savait pas s'il restait quelque chose à enregistrer). La référence se
     calcule depuis la fiche et le détail lus, pas depuis une copie : après un
     enregistrement, leur relecture la remet d'elle-même au niveau de la saisie. */
  /* Deux EMPREINTES, une par rubrique de la page : la composition (paramètres,
     nature et ce qui va avec) et la réalisation (délai, jours, rendez-vous).
     Chacune a son bouton, actif seulement si ELLE a changé. La référence se
     calcule depuis la fiche et le détail lus, pas depuis une copie : après un
     enregistrement, leur relecture la remet d'elle-même au niveau de la saisie. */
  const empreinteComposition = (v: { lies: string[]; nature: string; formule: string; composantes: string[]; repertoire: string[]; jeuDeRegles: string }) =>
    JSON.stringify([[...v.lies].sort(), v.nature, v.formule.trim(), [...v.composantes].sort(), [...v.repertoire].sort(), v.jeuDeRegles.trim()]);
  const empreinteRealisation = (v: { delai: string; jours: string[]; surRdv: boolean; matin: string; matinDe: string; matinA: string }) =>
    JSON.stringify([v.delai.trim(), [...v.jours].sort(), v.surRdv, v.matin, v.matinDe.trim(), v.matinA.trim()]);
  const dLu = detail as unknown as { nature?: string | null; jeuDeRegles?: string | null; formule?: string | null; composantes?: { documentId: string }[]; repertoire?: { documentId: string }[] } | null;
  const compositionModifiee = !!dLu && calculAmorce && empreinteComposition({ lies, nature, formule, composantes, repertoire, jeuDeRegles }) !== empreinteComposition({
    lies: fiche.analytes.map((a) => a.documentId),
    nature: dLu.nature || 'primaire', formule: dLu.formule || '',
    composantes: (dLu.composantes || []).map((c) => c.documentId),
    repertoire: (dLu.repertoire || []).map((r) => r.documentId), jeuDeRegles: dLu.jeuDeRegles || '',
  });
  const realisationModifiee = empreinteRealisation({ delai, jours, surRdv, matin, matinDe, matinA }) !== empreinteRealisation({
    delai: fiche.delaiRenduHeures === null ? '' : String(fiche.delaiRenduHeures),
    jours: fiche.joursRealisation || [], surRdv: !!fiche.preanalytique?.surRendezVous,
    matin: ['obligatoire', 'preference'].includes(String(lirePre('matin'))) ? String(lirePre('matin')) : '',
    matinDe: heureTexte(lirePre('matinDe')), matinA: heureTexte(lirePre('matinA')),
  });
  const reglagesModifies = compositionModifiee || realisationModifiee;

  /* L'enregistrement des réglages, PAR PARTIE : la page n'envoie que la
     rubrique dont on a cliqué le bouton ; l'ancienne disposition envoie tout. */
  const [faitPour, setFaitPour] = useState<'composition' | 'realisation' | 'tout' | ''>('');
  const [partieEnvoi, setPartieEnvoi] = useState<'composition' | 'realisation' | 'tout'>('tout');
  /* LA DISPOSITION — onglets (l'actuelle) ou page à rubriques (l'essai). L'essai
     s'ouvre par l'adresse, `?essai=page`, le temps qu'Ines le valide : l'équipe
     garde les onglets d'ici là. Lu après le montage — le rendu serveur ne voit
     pas l'adresse. */
  const [disposition, setDisposition] = useState<'onglets' | 'page'>('onglets');
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('essai') === 'page') setDisposition('page');
  }, []);
  const enregistrer = (partie: 'composition' | 'realisation' | 'tout' = 'tout') => {
    setEnvoi(true); setErreur(''); setReglagesFaits(''); setFaitPour(''); setPartieEnvoi(partie);
    const h = delai.trim() === '' ? null : Number(delai);
    void (async () => {
      if (partie !== 'realisation') {
        const r1 = await moteur(`manuel-ref/fiches/${fiche.documentId}/analytes`, { analyteIds: lies });
        if (!r1.ok) { setErreur(r1.message); setEnvoi(false); return; }
      }
      if (partie !== 'composition') {
        const r2 = await moteur(`manuel-ref/fiches/${fiche.documentId}/champs`, {
          delaiRenduHeures: h, joursRealisation: jours.length ? jours : null, affichageTechniques: affichage,
          // Le reste de `preanalytique` est REPOSTÉ tel quel : seuls la case et
          // l'horaire changent. Une heure vide part à null.
          preanalytique: {
            ...(fiche.preanalytique || {}), surRendezVous: surRdv,
            matin: matin || null,
            matinDe: matin && matinDe.trim() ? Number(matinDe.replace(',', '.')) : null,
            matinA: matin && matinA.trim() ? Number(matinA.replace(',', '.')) : null,
          },
        });
        if (!r2.ok) { setErreur(r2.message); setEnvoi(false); return; }
      }
      // Le bloc calcul ne part que si le détail a été lu : sinon on écraserait
      // l'état serveur avec les valeurs d'amorçage à vide.
      if (partie !== 'realisation' && calculAmorce) {
        /* Une seule route pour la structure : nature + formule/composantes
           (calculé) + répertoire/jeu de règles (panel à règles). Même garde :
           elle ne part que si le détail a été lu, sinon un antibiogramme
           perdrait ses 40 molécules au premier enregistrement d'un champ sans
           rapport. */
        const r3 = await moteur(`manuel-ref/fiches/${fiche.documentId}/structure`, {
          nature, formule: formule.trim() || null, composanteIds: composantes,
          repertoireIds: repertoire, jeuDeRegles: jeuDeRegles.trim() || null,
        });
        if (!r3.ok) { setErreur(r3.message); setEnvoi(false); return; }
      }
      setEnvoi(false);
      setReglagesFaits('Réglages enregistrés — appliqués immédiatement.');
      setFaitPour(partie);
      /* La page RESTE ouverte (plus de fenêtre qui se referme) : on relit le
         détail et la liste, et l'on continue. */
      apresEcriture();
    })();
  };

  /* Les CHAMPS des réglages, écrits une fois et posés par les deux dispositions
     (onglets, ou page à rubriques — essai du 31/10/2026). */
  const champParametres = (
    <>
                <SelecteurPastilles
                  options={analytes.map((a) => ({ documentId: a.documentId, code: a.code, nom: a.nom }))}
                  choisis={lies} onChange={setLies} modifiable={estRq}
                  placeholder="Ajouter un paramètre — code ou nom…"
                  ariaLabel="Rechercher un paramètre à rattacher à l’examen"
                  unite="paramètre"
                  vide="Aucun — examen non composé." />
    </>
  );
  const champRealisation = (
    <>
                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                  <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                    {/* Un délai est un MAXIMUM auquel on s'engage (Ines, 19/10/2026) :
                        on saisit des heures, on relit à côté ce que la fiche annonce. */}
                    <span>Délai de rendu maximal (heures)</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <input type="number" min={0} max={2160} value={delai} disabled={!estRq}
                        onChange={(e) => setDelai(e.currentTarget.value)} style={{ ...inputStyle, width: 110, fontSize: 12.5 }} />
                      <span style={{ color: 'var(--text-muted)' }}>{delai.trim() ? `affiché « ${formaterDelai(Number(delai), 'phrase')} »` : 'sans délai'}</span>
                    </span>
                  </label>
                  <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                    <span>Jours de réalisation</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
                      {JOURS.map((j) => (
                        <label key={j} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', cursor: estRq ? 'pointer' : 'default' }}>
                          <input type="checkbox" checked={jours.includes(j)} disabled={!estRq}
                            onChange={(e) => setJours(e.currentTarget.checked ? [...jours, j] : jours.filter((x) => x !== j))} />
                          {j}
                        </label>
                      ))}
                    </div>
                    {/* Aucun jour coché = tous les jours (`formaterJours`) : le dire,
                        sinon la lecture « tous les jours d'ouverture » semble
                        contredire des cases vides (relevé du 29/10/2026). */}
                    {jours.length === 0 && (
                      <span style={{ color: 'var(--text-muted)' }}>Aucun jour coché : la fiche annonce « tous les jours d’ouverture ».</span>
                    )}
                  </div>
                  {/* SUR RENDEZ-VOUS (Ines, 20/10/2026) — montré aux patients sur la fiche. */}
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, alignSelf: 'end', cursor: estRq ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={surRdv} disabled={!estRq} onChange={(e) => setSurRdv(e.currentTarget.checked)} />
                    Sur rendez-vous
                  </label>
                  {/* HEURE DE PRÉLÈVEMENT — le site prévient le patient, il ne
                      l'oblige pas ; l'équipe confirme au téléphone. */}
                  <div style={{ display: 'grid', gap: 5, fontSize: 12.5, gridColumn: '1 / -1' }}>
                    <span style={{ fontWeight: 600 }}>Heure de prélèvement</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <select value={matin} disabled={!estRq} aria-label="Heure de prélèvement"
                        onChange={(e) => setMatin(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
                        <option value="">Indifférente</option>
                        <option value="preference">Le matin, de préférence</option>
                        <option value="obligatoire">Le matin, obligatoirement</option>
                      </select>
                      {matin && (
                        <>
                          <span>entre</span>
                          <input value={matinDe} disabled={!estRq} inputMode="decimal" placeholder="7" aria-label="Heure de début"
                            onChange={(e) => setMatinDe(e.currentTarget.value)} style={{ ...inputStyle, width: 56, fontSize: 12.5 }} />
                          <span>h et</span>
                          <input value={matinA} disabled={!estRq} inputMode="decimal" placeholder="9" aria-label="Heure de fin"
                            onChange={(e) => setMatinA(e.currentTarget.value)} style={{ ...inputStyle, width: 56, fontSize: 12.5 }} />
                          <span>h</span>
                        </>
                      )}
                    </div>
                    <span style={{ color: 'var(--text-muted)' }}>
                      Le formulaire de rendez-vous oriente le patient vers le matin (sans l’y obliger) ; l’heure figure sur la demande que l’équipe confirme. Heures facultatives — « 7,5 » pour 7 h 30.
                    </span>
                  </div>
                </div>
    </>
  );
  const champNature = (
    <>
                {!calculAmorce ? <Skeleton rows={1} /> : (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <select value={nature} disabled={!estRq} aria-label="Nature de l’examen"
                      onChange={(e) => setNature(e.currentTarget.value)} style={{ ...inputStyle, width: '100%', fontSize: 12.5 }}>
                      {NATURES_EXAMEN.map((n) => <option key={n.code} value={n.code}>{n.libelle}</option>)}
                    </select>
                    <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{NATURES_EXAMEN.find((n) => n.code === nature)?.aide}</p>
                    {nature === 'calcule' && (
                      <>
                        <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                          <span>Formule (telle qu’elle se lit sur le compte rendu)</span>
                          <input type="text" value={formule} disabled={!estRq} maxLength={400}
                            placeholder="Ex. CKD-EPI 2021 : DFG = 142 × min(Scr/κ,1)^α × …"
                            onChange={(e) => setFormule(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }} />
                        </label>
                        <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                          <span>Composantes — les analyses dont ce résultat dépend</span>
                          <SelecteurPastilles
                            options={catalogue
                              .filter((c) => c.documentId !== fiche.documentId && !c.archivee)
                              .map((c) => ({ documentId: c.documentId, code: c.code, nom: c.libelle }))}
                            choisis={composantes} onChange={setComposantes} modifiable={estRq}
                            placeholder="Ajouter une analyse — code ou libellé…"
                            ariaLabel="Rechercher une analyse dont ce résultat dépend"
                            unite="analyse"
                            vide="Aucune — la formule ne cite encore aucune analyse du catalogue." />
                        </div>
                      </>
                    )}
                    {(nature === 'panel_regles' || nature === 'panel_carte') && (
                      <>
                        {nature === 'panel_regles' && (
                          <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                            <span>Jeu de règles (code, Paramétrage → jeux de règles)</span>
                            <input type="text" value={jeuDeRegles} disabled={!estRq} maxLength={60} placeholder="ATB_CASFM_2025"
                              onChange={(e) => setJeuDeRegles(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5, fontFamily: 'var(--font-mono)' }} />
                          </label>
                        )}
                        <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
                          <span>{nature === 'panel_regles' ? 'Répertoire — ce que le laboratoire sait tester (rendu selon les règles)' : 'Répertoire — ce que le prescripteur peut choisir'}</span>
                          <SelecteurPastilles
                            options={parametres}
                            choisis={repertoire} onChange={setRepertoire} modifiable={estRq}
                            placeholder="Ajouter un paramètre — code ou nom…"
                            ariaLabel="Rechercher un paramètre à porter au répertoire"
                            unite="paramètre"
                            vide="Répertoire vide — le laboratoire ne déclare encore rien de testable." />
                        </div>
                      </>
                    )}
                  </div>
                )}
    </>
  );

  const libelleChamp = (c: string) => FIELD_CATALOG.find((f) => f.key === c)?.label || c;
  const nbEnVigueur = (detail?.intervalles.filter((i) => !i.dateFin).length ?? fiche.nbIntervallesEnVigueur) || 0;
  const nbCqi = cartesCqi.filter((c) => fiche.analytes.some((a) => a.code === c.analyte)).length;

  /* ── La page à rubriques : les rubriques, dans l'ordre du sommaire ── */
  const piedReglages = (partie: 'composition' | 'realisation', modifie: boolean) => estRq && (
    <div style={{ display: 'grid', gap: 8, marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border-default)' }}>
      {erreur && partieEnvoi === partie && (
        <div role="alert" style={{
          padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'var(--red-50)',
          border: '1px solid var(--red-200)', color: 'var(--red-600)', fontSize: 12.5, lineHeight: 1.5,
        }}>{erreur}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span role="status" style={{ flex: '1 1 200px', fontSize: 12.5, color: !modifie && faitPour === partie ? 'var(--green-700)' : 'var(--text-muted)' }}>
          {modifie ? 'Modifications non enregistrées — visibles sur le catalogue public dès l’enregistrement.'
            : faitPour === partie ? 'Appliqué.' : 'Rien à enregistrer.'}
        </span>
        <PiedActions taille="sm"
          principale={{ libelle: envoi && partieEnvoi === partie ? 'Enregistrement…' : 'Appliquer', disabled: envoi || !modifie,
            onClick: () => enregistrer(partie), title: modifie ? undefined : 'Rien n’a changé dans cette rubrique' }} />
      </div>
    </div>
  );
  const dTexte = (detail || {}) as unknown as Record<string, unknown>;
  const principeCalcule = ((dTexte.principeMethode as string[] | undefined) || []).join(' · ');
  /* LES CONTRADICTIONS que la fiche peut voir elle-même, listées en tête plutôt
     que laissées à la découverte (relevé du 29/10/2026 : « GOD-POD » d'un côté,
     « Hexokinase » de l'autre, sur la même fiche). */
  const contradictions: React.ReactNode[] = [];
  {
    const sansAcc = (x: string) => x.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    const libre = String(dTexte.principe || '').trim();
    const mots = sansAcc(principeCalcule).split(/[^a-z0-9]+/).filter((m) => m.length > 3);
    if (libre && principeCalcule && !mots.some((m) => sansAcc(libre).includes(m))) {
      contradictions.push(<>Principe : le texte saisi dit « {libre} », les techniques disent « {principeCalcule} ».</>);
    }
    const retard = enRetard(fiche, fiche.analytes);
    if (retard.length) {
      contradictions.push(<>{retard.map((c) => c.code).join(', ')} porte(nt) une version plus récente que l’examen : la montée de version n’a pas été propagée.</>);
    }
  }
  const nbRefus = detail?.criteresEchantillon.length ?? fiche.nbCriteres;
  const rubriquesPage: RubriqueDePage[] = !detail ? [] : [
    {
      id: 'composition', groupe: 'Examen', titre: 'Composition', marque: 'direct',
      aide: <>Les paramètres que l’examen rend et sa nature. Plusieurs paramètres = un examen <strong>composé</strong> (ionogramme → Na, K, Cl).</>,
      contenu: (
        <div style={{ display: 'grid', gap: 14 }}>
          {fiche.analytes.length > 0 && (
            <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
              <span style={{ color: 'var(--text-muted)' }}>Ouvrir un paramètre — unités, prélèvement, stabilités, valeurs de référence :</span>
              <PucesParametres fiche={fiche} />
            </div>
          )}
          <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-muted)' }}>Paramètres rattachés</span>
            {champParametres}
          </div>
          <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-muted)' }}>Nature de l’examen</span>
            {champNature}
          </div>
          {piedReglages('composition', compositionModifiee)}
        </div>
      ),
    },
    {
      id: 'prelevement', groupe: 'Examen', titre: 'Prélèvement', marque: 'relecture',
      aide: 'Le milieu prélevé, les contenants acceptés à réception, ce qui est commun. Opposable au préleveur : passe par le circuit.',
      contenu: <PrelevementReception fiche={fiche} detail={detail as unknown as Record<string, unknown>} estRq={estRq} onEcrit={apresEcriture} />,
    },
    {
      id: 'realisation', groupe: 'Examen', titre: 'Réalisation & délai', marque: 'direct',
      aide: 'Ce que le patient et le prescripteur lisent sur la fiche publique : délai de rendu, jours, rendez-vous.',
      contenu: (
        <div style={{ display: 'grid', gap: 12 }}>
          {champRealisation}
          <div style={{ fontSize: 12.5, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <span style={{ color: 'var(--text-muted)' }}>Principe de la méthode</span>
            <span>{principeCalcule || '—'}</span>
            <Pastille compacte ton="muted" title={MARQUES.lecture.aide}>Calculé</Pastille>
            <span style={{ color: 'var(--text-faint)', fontSize: 11.5 }}>déduit des techniques — rubrique Techniques</span>
          </div>
          {piedReglages('realisation', realisationModifiee)}
        </div>
      ),
    },
    {
      id: 'patients', groupe: 'Examen', titre: 'Textes patients', marque: 'direct',
      contenu: <VoletPatients fiche={fiche} detail={detail as unknown as Record<string, unknown>} estRq={estRq}
        brouillon={brouillon ?? null} listes={listes} opposables={opposables} onEcrit={apresEcriture} />,
    },
    {
      id: 'catalogue', groupe: 'Examen', titre: 'Identité, tarifs, accréditation', marque: 'relecture',
      contenu: <DossierExamen page fiche={fiche} detail={detail} dvms={dvms} estRq={estRq}
        brouillon={brouillon ?? null} listes={listes} opposables={opposables} onEcrit={apresEcriture} />,
    },
    {
      id: 'valeurs', groupe: 'Venant des paramètres', titre: 'Valeurs de référence', marque: 'validation', compteur: nbEnVigueur,
      aide: 'Les intervalles en vigueur, par paramètre — ceux que la fiche publique affiche.',
      contenu: (
        <div style={{ display: 'grid', gap: 10, fontSize: 12.5 }}>
          <ValeursParParametre detail={detail as unknown as Record<string, unknown>} />
          <details>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Proposer, valider ou clore un intervalle</summary>
            <div style={{ marginTop: 8 }}><OngletIntervalles fiche={detail} estRq={estRq} onEcrit={apresEcriture} /></div>
          </details>
        </div>
      ),
    },
    { id: 'techniques', groupe: 'Qualité', titre: 'Techniques', marque: 'direct', repliee: true, compteur: detail.techniques.length,
      contenu: <OngletTechniques fiche={detail} estRq={estRq} onEcrit={apresEcriture} /> },
    { id: 'cqi', groupe: 'Qualité', titre: 'CQI', marque: 'lecture', repliee: true, compteur: nbCqi,
      contenu: <OngletCqi analytes={parametresLies} cartesCqi={cartesCqi} /> },
    { id: 'recevabilite', groupe: 'Qualité', titre: 'Refus d’échantillon', marque: 'direct', repliee: true, compteur: nbRefus,
      aide: 'Les motifs pour lesquels un échantillon est refusé à réception — à ne pas confondre avec les critères de performance analytique.',
      contenu: <OngletCriteresFiche fiche={detail} estRq={estRq} onEcrit={apresEcriture} /> },
    { id: 'performance', groupe: 'Qualité', titre: 'Performance', marque: 'lecture', repliee: true,
      contenu: <PerformanceFiche fiche={{ ...detail, analytes: fiche.analytes.map((a) => ({ code: a.code, nom: a.nom })) }} /> },
    { id: 'interpretation', groupe: 'Qualité', titre: 'Interprétation biologique', marque: 'lecture', repliee: true,
      contenu: <OngletInterpretation code={fiche.code} parametresDeLExamen={fiche.analytes.map((a) => a.code)} /> },
  ];
  const visiblePatients = dTexte.visiblePatients === 'Oui';

  return (
    <div>
      {/* Le retour vers la liste, en haut : l'adresse redevient celle de la
          liste, les filtres et le défilement de la liste sont conservés par
          l'écran parent. Le bouton « Précédent » du navigateur fait la même
          chose. */}
      <button type="button" onClick={onFerme} className="ged-btn"
        title="Revenir à la liste des examens. La flèche « précédent » du navigateur fait la même chose."
        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)' }}>
        <I n="list" s={15} />Retour à la liste des examens
      </button>
      <div style={{ marginTop: 10 }}>
        <TitrePage
          eyebrow={fiche.famille || 'Examen'}
          titre={<><span style={{ fontFamily: 'var(--font-mono)' }}>{fiche.code}</span> — {fiche.libelle}</>}
          sousTitre={disposition === 'page' ? (
            /* Dans la page, l'ÉTAT de publication en mots, pas des numéros
               (relevé du 29/10/2026 : « GLY v4 — GLY v3 », sans dire à quoi
               chacun correspond). Les versions restent dans l'infobulle. */
            <span style={{ display: 'inline-flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
              title={`Versions : ${libelleComposition(fiche, fiche.analytes)}`}>
              {fiche.archivee
                ? <Pastille compacte ton="neutral">Retiré du catalogue</Pastille>
                : <Pastille compacte ton="success">Publié au catalogue</Pastille>}
              {!fiche.archivee && detail && (visiblePatients
                ? <Pastille compacte ton="info" title="Dans la liste courte : les patients trouvent la fiche sur le site.">Visible des patients</Pastille>
                : <Pastille compacte ton="muted" title="Hors liste courte : seuls les correspondants connectés la voient.">Réservé aux correspondants</Pastille>)}
            </span>
          ) : (
            /* La version de l'examen AVEC celles de ses composants. Un numéro
               isolé ne dit pas ce qui a bougé ; la ligne complète, si. */
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{libelleComposition(fiche, fiche.analytes)}</span>
          )}
          actions={(
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {fiche.archivee && <Pastille ton="neutral" title="Retirée du catalogue publié">retirée</Pastille>}
              <a href={`${MANUEL_URL}/fiche/${encodeURIComponent(fiche.code)}?apercu=public`}
                target="_blank" rel="noopener noreferrer" className="ged-btn"
                title="La fiche telle que le public la lit, dans un nouvel onglet"
                style={{ ...styleBouton('contour', 'sm'), textDecoration: 'none' }}>
                Voir la fiche publique ↗
              </a>
            </div>
          )}
        />
      </div>
      {/* Un composant plus récent que son examen ne peut pas exister : la montée
          de version se propage. S'il s'en affiche un, c'est que la propagation a
          été contournée — et que le numéro de l'examen est faux. On le DIT. */}
      {disposition === 'onglets' && enRetard(fiche, fiche.analytes).length > 0 && (
        <div style={{ fontSize: 12.5, marginTop: 8, color: 'var(--red-700)' }}>
          ⚠ {enRetard(fiche, fiche.analytes).map((c) => c.code).join(', ')} porte(nt) une version
          plus récente que l’examen : la montée de version n’a pas été propagée, le numéro affiché
          ci-dessus n’est pas à jour.
        </div>
      )}

      {/* L'ÉTAT de la fiche : ce qui attend dans le circuit, et les actes. */}
      {brouillon && (
        <BandeauCircuit code={fiche.code} brouillon={brouillon} estRq={estRq} moi={moi}
          libelles={libelleChamp} onFait={apresEcriture} />
      )}

      {disposition === 'page' ? (
        !detail ? <div style={{ marginTop: 14 }}><Skeleton rows={6} /></div> : (
          <DispositionPage prefixe={`examen-${fiche.code}`} rubriques={rubriquesPage}
            entete={contradictions.length > 0 && (
              <details open style={{ ...card, padding: '10px 16px', background: 'var(--amber-50)' }}>
                <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Pastille compacte ton="warning">{contradictions.length}</Pastille>
                  {contradictions.length > 1 ? 'contradictions à trancher' : 'contradiction à trancher'}
                </summary>
                <ul style={{ margin: '8px 0 0', paddingInlineStart: 20, fontSize: 12.5, lineHeight: 1.6, display: 'grid', gap: 3 }}>
                  {contradictions.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </details>
            )} />
        )
      ) : (<>
      <div style={{ marginTop: 14 }}>
        <OngletsDossier<OngletFiche>
          id={`examen-${fiche.documentId}`}
          actif={ongletFiche}
          onChange={setOngletFiche}
          items={[
            { k: 'prelevement', label: 'Prélèvement' },
            { k: 'realisation', label: 'Réalisation & rendu' },
            { k: 'patients', label: 'Patients & diffusion' },
            { k: 'qualite', label: 'Qualité' },
          ]} />
      </div>

      <PanneauDossier id={`examen-${fiche.documentId}`} actif={ongletFiche}>
        {!detail && <Skeleton rows={4} />}

        {/* ── 1. PRÉLÈVEMENT — le milieu prélevé, ce qu'on accepte à réception,
            le commun. Écrit au brouillon : ces champs sont opposables au
            préleveur. */}
        {ongletFiche === 'prelevement' && detail && (
          <PrelevementReception fiche={fiche} detail={detail as unknown as Record<string, unknown>} estRq={estRq} onEcrit={apresEcriture} />
        )}

        {/* ── 2. RÉALISATION & RENDU — identité, délais, tarifs, accréditation,
            puis les réglages de structure (paramètres, délai, jours, nature). */}
        {ongletFiche === 'realisation' && detail && (
          <>
            <DossierExamen fiche={fiche} detail={detail} dvms={dvms} estRq={estRq}
              brouillon={brouillon ?? null} listes={listes} opposables={opposables} onEcrit={apresEcriture} />
            <div style={{ marginTop: 18, display: 'grid', gap: 10 }}>
              <div>
                <TitreRubrique style={{ marginBottom: 0 }}>Réglages de structure</TitreRubrique>
                <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '88ch' }}>
                  Ce que la fiche <strong>publique</strong> annonce — composition, délai, jours, nature. Réglages du
                  référentiel : appliqués immédiatement, sans passer par le circuit.
                </p>
              </div>
              <SectionReglage titre="Paramètres de l’examen (mesurande ou analyte)"
                aide={<>Plusieurs paramètres = un examen <strong>composé</strong> (ionogramme → Na, K, Cl). Tout retirer délie l’examen.</>}>
                {champParametres}
              </SectionReglage>

              <SectionReglage titre="Réalisation"
                aide="Le délai de rendu, les jours de réalisation et le rendez-vous — ce que le patient et le prescripteur lisent sur la fiche publique.">
                {champRealisation}
              </SectionReglage>

              <SectionReglage titre="Nature de l’examen"
                aide="Ce que l’examen rend, et qui décide de sa liste : mesuré, calculé, panel fixe, panel à règles, panel à la carte.">
                {champNature}
              </SectionReglage>

              {/* `role="alert"` : l'échec d'un enregistrement doit être ANNONCÉ, et
                  non seulement écrit. Il se lit juste au-dessus du bouton qui
                  vient de le produire. */}
              {erreur && (
                <div role="alert" style={{
                  padding: '10px 12px', borderRadius: 'var(--radius-md)',
                  background: 'var(--red-50)', border: '1px solid var(--red-200)',
                  color: 'var(--red-600)', fontSize: 12.5, lineHeight: 1.5,
                }}>{erreur}</div>
              )}
              {estRq && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span role="status" style={{ flex: '1 1 200px', fontSize: 12.5, color: reglagesFaits && !reglagesModifies ? 'var(--green-700)' : 'var(--text-muted)' }}>
                    {reglagesModifies ? 'Modifications non enregistrées — appliquées dès l’enregistrement.' : (reglagesFaits || 'Rien à enregistrer.')}
                  </span>
                  <PiedActions taille="sm"
                    principale={{ libelle: envoi ? 'Enregistrement…' : 'Enregistrer les réglages', disabled: envoi || !reglagesModifies, onClick: () => enregistrer('tout'),
                      title: reglagesModifies ? undefined : 'Aucun réglage modifié' }} />
                </div>
              )}
            </div>
          </>
        )}

        {/* ── 3. PATIENTS & DIFFUSION — la liste courte, les deux textes
            patient, et la fiche publique telle qu'elle se lit. */}
        {ongletFiche === 'patients' && detail && (
          <VoletPatients fiche={fiche} detail={detail as unknown as Record<string, unknown>} estRq={estRq}
            brouillon={brouillon ?? null} listes={listes} opposables={opposables} onEcrit={apresEcriture} />
        )}

        {/* ── 4. QUALITÉ — ce qui sert le technicien et l'accréditation : repli
            derrière un sélecteur, pour ne pas noyer la mise à jour du
            catalogue. */}
        {ongletFiche === 'qualite' && detail && (
          <div style={{ display: 'grid', gap: 12 }}>
            <Segment ariaLabel="Rubrique qualité de l’examen" valeur={sousQualite} onChange={setSousQualite}
              options={[
                /* « Au lieu de Liaisons, je veux voir CQI, et on voit si un CQI
                   est disponible ou pas » (RQ, 08/09/2026). */
                { valeur: 'cqi' as const, libelle: 'CQI', compteur: nbCqi },
                // Nos performances RÉELLES (celles du CQI) d'abord, celles des
                // référentiels externes (SFBC/RICOS/CLIA) juste à côté.
                { valeur: 'performance' as const, libelle: 'Performance' },
                { valeur: 'techniques' as const, libelle: 'Techniques', compteur: detail.techniques.length },
                { valeur: 'intervalles' as const, libelle: 'Intervalles', compteur: nbEnVigueur },
                // Les critères d'ACCEPTATION de l'échantillon — à ne pas confondre
                // avec les critères de performance ci-dessus.
                { valeur: 'criteres' as const, libelle: 'Critères', compteur: detail.criteresEchantillon.length },
                // « Interprétation biologique » : le référentiel porte DÉJÀ un
                // onglet « Interprétation » — l'éditeur des jeux de règles.
                { valeur: 'interpretation' as const, libelle: 'Interprétation biologique' },
              ]} />
            {sousQualite === 'cqi' && <OngletCqi analytes={parametresLies} cartesCqi={cartesCqi} />}
            {sousQualite === 'performance' && (
              <PerformanceFiche fiche={{ ...detail, analytes: fiche.analytes.map((a) => ({ code: a.code, nom: a.nom })) }} />
            )}
            {sousQualite === 'techniques' && <OngletTechniques fiche={detail} estRq={estRq} onEcrit={apresEcriture} />}
            {sousQualite === 'intervalles' && <OngletIntervalles fiche={detail} estRq={estRq} onEcrit={apresEcriture} />}
            {sousQualite === 'criteres' && <OngletCriteresFiche fiche={detail} estRq={estRq} onEcrit={apresEcriture} />}
            {sousQualite === 'interpretation' && (
              <OngletInterpretation code={fiche.code} parametresDeLExamen={fiche.analytes.map((a) => a.code)} />
            )}
          </div>
        )}
      </PanneauDossier>
      </>)}
    </div>
  );
}

/**
 * PATIENTS & DIFFUSION — ce que le public lit (Ines, 16-17/10/2026).
 *
 * Trois champs décident de la fiche patient : la LISTE COURTE (« Oui » et rien
 * d'autre ouvre la fiche au public, côté serveur), « À quoi sert cet examen »
 * et « Comment me préparer », une consigne par ligne. Ce sont des décisions de
 * PUBLICATION du RQ : elles s'écrivent en direct, sans circuit — la machinerie
 * est celle du Dossier (clic sur la valeur, `EditionChamp`).
 *
 * Et juste dessous, la fiche telle que le patient la voit : RIEN au-dessus du
 * cadre (RQ, 19/08) — le moindre bouton de l'application fausserait le jugement.
 */
function VoletPatients({ fiche, detail, estRq, brouillon, listes, opposables, onEcrit }: {
  fiche: LigneCatalogue; detail: Record<string, unknown>; estRq: boolean;
  brouillon: BrouillonFiche | null; listes: ListesDuDossier; opposables: string[]; onEcrit: () => void;
}) {
  const d = detail;
  const [champ, setChamp] = useState<string | null>(null);
  const editable = estRq ? {
    onEditer: setChamp,
    champEnCours: champ,
    editeur: (c: string) => (
      <EditionChamp fiche={fiche} champ={c} valeur={brouillon && c in brouillon.values ? brouillon.values[c] : d[c]} listes={listes}
        opposables={opposables} onFerme={() => setChamp(null)} onEcrit={onEcrit} />
    ),
  } : {};
  const val = (c: string, publie: React.ReactNode) => (brouillon && c in brouillon.values
    ? <ValeurAvecBrouillon publie={publie} brouillon={texteDe(c, brouillon.values[c], listes)} /> : publie);
  const puces = (v: unknown) => {
    const lignes = String(v ?? '').split('\n').map((x) => x.trim()).filter(Boolean);
    return lignes.length ? (
      <ul style={{ margin: 0, paddingInlineStart: 18, display: 'grid', gap: 2 }}>
        {lignes.map((l, i) => <li key={i}>{l}</li>)}
      </ul>
    ) : null;
  };
  const visible = d.visiblePatients === 'Oui';
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        {visible
          ? <>Cet examen est dans la <strong>liste courte</strong> : les patients le trouvent sur le site, avec les deux textes ci-dessous. Le reste de la fiche est réservé aux correspondants connectés.</>
          : <>Cet examen n’est <strong>pas</strong> dans la liste courte : seuls les correspondants connectés le voient. Passez « Visible des patients » à « Oui » pour l’ouvrir au public.</>}
        {estRq && !champ && <> Cliquez une ligne pour la modifier — appliqué immédiatement.</>}
      </div>
      <Rubrique {...editable} titre="Patients" lignes={[
        ['Visible des patients (liste courte)', val('visiblePatients', d.visiblePatients as string), 'visiblePatients'],
        ['À quoi sert cet examen', val('patientUtilite', d.patientUtilite as string), 'patientUtilite'],
        ['Comment me préparer', val('patientPreparation', puces(d.patientPreparation)), 'patientPreparation'],
      ]} />
      {/* Le jeûne, l'heure et le recueil, en CHAMPS (31/10/2026) — voir le fichier. */}
      <PreparationPatient documentId={fiche.documentId} code={fiche.code} preanalytique={fiche.preanalytique} estRq={estRq} onEcrit={onEcrit} />
      <div>
        <TitreRubrique>La fiche publique, telle qu’elle se lit</TitreRubrique>
        <ApercuPublic code={fiche.code} />
      </div>
    </div>
  );
}

type ChampVocabulaire = { cle: string; libelle: string; options: { code: string; libelle: string }[] };

/** Une liste fermée du vocabulaire du prélèvement. Hors de `FormExamen` : un
 *  composant déclaré dans un autre est recréé à chaque rendu, et la liste
 *  perd alors son focus à chaque frappe ailleurs dans la fenêtre. */
function ListeVocabulaire({ cle, champ, valeur, poser }: {
  cle: string; champ: ChampVocabulaire | undefined; valeur: string; poser: (x: string) => void;
}) {
  return (
    <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
      <span>{champ?.libelle || cle}</span>
      <select value={valeur} onChange={(e) => poser(e.currentTarget.value)} style={{ ...inputStyle, fontSize: 12.5 }}>
        <option value="">—</option>
        {(champ?.options || []).map((o) => <option key={o.code} value={o.code}>{o.libelle}</option>)}
      </select>
    </label>
  );
}

/**
 * CRÉER un examen — le bouton manquait aussi (RQ, 18/08/2026). Il n'existait
 * aucune route d'écriture : un catalogue qu'on ne peut pas alimenter à l'écran
 * n'est pas tenu, il est subi.
 *
 * ── UN SEUL PARCOURS (RQ, 07/09/2026 : « ne pas avoir à cliquer partout dans
 *    différents référentiels ») ────────────────────────────────────────────
 * Trois temps dans la même fenêtre : l'identité, ses PARAMÈTRES (existants à
 * rattacher, ou le jumeau à créer avec sa matière, son tube, sa préparation),
 * et ce que le moteur en DÉDUIT — nature, points d'entrée — montré au retour.
 * Le serveur relie d'office au paramètre de même code s'il existe (invariant
 * « même code = même objet ») et propose les matériels depuis les paramètres
 * rattachés. La fiche s'ouvre ensuite sur son dossier : le reste (délai,
 * techniques, intervalles) se règle là, sans changer d'écran.
 */
function FormExamen({ onFerme, onCree, catalogue }: {
  onFerme: () => void; onCree: (code: string) => void;
  /** Le catalogue déjà chargé : un code pris se dit À LA SAISIE, pas au refus du moteur. */
  catalogue: { code: string; libelle: string; archivee: boolean }[];
}) {
  /* Ce que la personne a TAPÉ, à côté du code normalisé : « ZZ test » devient
     « ZZ_TEST », et cela se montre au lieu de se faire en silence (28/10/2026). */
  const [saisieCode, setSaisieCode] = useState('');
  const [v, setV] = useState({
    code: '', libelle: '', famille: '', echantillon: '', recipient: '', delaiRenduHeures: '', cotation: '',
  });
  const [analytes, setAnalytes] = useState<string[]>([]);
  const [parametres, setParametres] = useState<{ documentId: string; code: string; nom: string }[]>([]);
  const [jumeau, setJumeau] = useState<'aucun' | 'creer'>('aucun');
  const [pj, setPj] = useState({ nom: '', matiere: '', recipient: '', preparation: '', uniteDefaut: '' });
  const [vocabulaire, setVocabulaire] = useState<ChampVocabulaire[]>([]);
  const [familles, setFamilles] = useState<string[]>([]);
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState<{ code: string; parametres: number; materiels: string[]; parametreCree: boolean } | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const verdict = verifierCode(v.code);
  const ampute = motTronque(v.code);

  useEffect(() => {
    let vivant = true;
    void (async () => {
      const out: { documentId: string; code: string; nom: string }[] = [];
      for (let page = 1; page <= 20; page += 1) {
        const res = await fetch(`/api/ged/ged-analytes?pagination[page]=${page}&pagination[pageSize]=100&sort=code:asc&fields[0]=code&fields[1]=nom`, { credentials: 'include' }).catch(() => null);
        if (!res || !res.ok) break;
        const d = await res.json().catch(() => null);
        const lot = ((d?.data as Record<string, unknown>[]) || []);
        out.push(...lot.map((a) => ({ documentId: String(a.documentId), code: String(a.code), nom: String(a.nom) })));
        if (lot.length < 100) break;
      }
      if (vivant) setParametres(out);
    })();
    /* Le vocabulaire du prélèvement part AVEC les paramètres (matière, tube,
       préparation en listes fermées) : la même source que le dossier du
       paramètre, pour que le jumeau naisse dans le bon vocabulaire. */
    void moteur('manuel-ref/parametres').then((r) => {
      if (!vivant || !r.ok) return;
      setVocabulaire(((r.data.vocabulaire as typeof vocabulaire) || []));
    });
    /* La famille est la discipline (07/09/2026) : liste PROJETÉE, jamais tapée. */
    void moteur('manuel-ref/parametrage').then((r) => {
      if (!vivant || !r.ok) return;
      setFamilles((((r.data.parametrage as Record<string, unknown> | null)?.lists as Record<string, string[]>)?.famille) || []);
    });
    return () => { vivant = false; };
  }, []);
  const voc = (cle: string) => vocabulaire.find((c) => c.cle === cle);
  const memeCode = parametres.find((p) => p.code === v.code);
  const examenPris = v.code ? catalogue.find((f) => f.code === v.code) : undefined;

  if (resultat) {
    return (
      <Modale titre={`Examen ${resultat.code} créé`} sousTitre="Ce que le moteur en a déduit." onClose={onFerme} sansSaisie
        pied={<Bouton variante="primaire" onClick={() => onCree(resultat.code)}>Ouvrir le dossier de l’examen</Bouton>}>
        <div style={{ display: 'grid', gap: 10, fontSize: 13, lineHeight: 1.55 }}>
          <div>{resultat.parametres} paramètre(s) rattaché(s){resultat.parametreCree ? ', dont le paramètre du même code, créé' : ''}.</div>
          <div>
            Transport & réception déduits de la matière et de la préparation des paramètres :{' '}
            {resultat.materiels.length
              ? resultat.materiels.map((m) => <code key={m} style={{ fontSize: 12, marginRight: 6 }}>{m}</code>)
              : <em>aucun — rattachez un paramètre avec une matière, ou déclarez-les dans Avant / après.</em>}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>
            Nature posée « primaire ». Délai, techniques, intervalles et nature (calculé, panel…) se règlent dans le dossier qui s’ouvre.
          </div>
        </div>
      </Modale>
    );
  }

  return (
    <Modale titre="Nouvel examen" sousTitre="Ce que le prescripteur commande — et ce qu’on mesure pour le rendre." onClose={onFerme} largeur={640}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>1 · Identité</div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
            <span>Code</span>
            <input value={v.code} autoFocus style={{ ...inputStyle, width: 190, fontFamily: 'var(--font-mono)' }}
              onChange={(e) => { setSaisieCode(e.currentTarget.value); setV({ ...v, code: normaliserCode(e.currentTarget.value) }); }} />
            {examenPris && (
              <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 3, maxWidth: 260 }}>
                Code déjà pris : <strong>{examenPris.code}</strong> — {examenPris.libelle}{examenPris.archivee ? ' (examen retiré, qui garde son code)' : ''}.
              </div>
            )}
            {!examenPris && v.code && !verdict.valide && <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 3 }}>{verdict.motif}</div>}
            {!examenPris && v.code && verdict.valide && saisieCode.trim() !== v.code && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, maxWidth: 260 }}>
                Sera enregistré <code>{v.code}</code> : majuscules, sans accent, espaces et points en « _ ».
              </div>
            )}
            {!!ampute.length && verdict.valide && (
              <div style={{ fontSize: 12.5, color: 'var(--amber-700)', marginTop: 3 }}>
                « {ampute.join(' », « ')} » ressemble à un mot coupé — préférez un sigle.
              </div>
            )}
          </label>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: 380 }}>
            {REGLES_CODE.map((r) => <div key={r}>· {r}</div>)}
          </div>
        </div>
        <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}><span>Libellé</span>
          <input value={v.libelle} onChange={(e) => setV({ ...v, libelle: e.currentTarget.value })} style={{ ...inputStyle, width: '100%' }} /></label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}><span>Famille (discipline)</span>
            <select value={v.famille} onChange={(e) => setV({ ...v, famille: e.currentTarget.value })} style={{ ...inputStyle, width: 190, fontSize: 12.5 }}>
              <option value="">— requise —</option>
              {familles.map((f) => <option key={f} value={f}>{f}</option>)}
            </select></label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}><span>Délai de rendu maximal (h)</span>
            <input type="number" min={0} max={2160} value={v.delaiRenduHeures} onChange={(e) => setV({ ...v, delaiRenduHeures: e.currentTarget.value })} style={{ ...inputStyle, width: 110 }} /></label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}><span>Cotation</span>
            <input value={v.cotation} onChange={(e) => setV({ ...v, cotation: e.currentTarget.value })} style={{ ...inputStyle, width: 110 }} /></label>
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginTop: 4 }}>2 · Ce qu’on mesure — les paramètres</div>
        {/* Code pris : annoncer un paramètre « relié d'office » ou proposer d'en
            créer le jumeau décrivait la création d'un examen qui n'aura pas
            lieu (relevé du 29/10/2026). La suite attend un code libre. */}
        {examenPris ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Choisissez d’abord un code libre : les paramètres proposés en dépendent.</div>
        ) : memeCode ? (
          <div style={{ fontSize: 12.5, padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
            Le paramètre <code>{memeCode.code}</code> ({memeCode.nom}) existe : il sera relié d’office — même code, même objet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
              <input type="radio" checked={jumeau === 'creer'} onChange={() => setJumeau('creer')} style={{ marginTop: 2 }} />
              <span>Créer un <strong>paramètre du même code</strong> {v.code ? <code>{v.code}</code> : ''} — l’examen mesure une seule grandeur, qui porte son nom (TSH, ferritine…)</span>
            </label>
            {jumeau === 'creer' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginLeft: 22 }}>
                <ListeVocabulaire cle="matiere" champ={voc('matiere')} valeur={pj.matiere} poser={(x) => setPj({ ...pj, matiere: x })} />
                <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}><span>Tube</span>
                  {/* La même liste que le dossier du paramètre : le tube ne se déduit pas de la matrice. */}
                  <select value={pj.recipient} onChange={(e) => setPj({ ...pj, recipient: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5 }}>
                    <option value="">—</option>
                    {['gel', 'sec', 'edta', 'citrate', 'heparine', 'fluorure', 'hemoc', 'seringue', 'urine', 'selles', 'ecouvillon', 'sterile', 'expecto', 'lcr', 'biopsie', 'cytobrosse'].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select></label>
                <ListeVocabulaire cle="preparation" champ={voc('preparation')} valeur={pj.preparation} poser={(x) => setPj({ ...pj, preparation: x })} />
                <label style={{ display: 'grid', gap: 4, fontSize: 12.5 }}><span>Unité par défaut</span>
                  <input value={pj.uniteDefaut} onChange={(e) => setPj({ ...pj, uniteDefaut: e.currentTarget.value })} style={{ ...inputStyle, fontSize: 12.5 }} placeholder="mUI/L" /></label>
              </div>
            )}
            <label style={{ display: 'flex', gap: 7, alignItems: 'flex-start', fontSize: 12.5, cursor: 'pointer' }}>
              <input type="radio" checked={jumeau === 'aucun'} onChange={() => setJumeau('aucun')} style={{ marginTop: 2 }} />
              <span>Pas de paramètre du même code — l’examen se compose de paramètres existants (ci-dessous), ou c’est un panel à déclarer ensuite</span>
            </label>
          </div>
        )}
        <div style={{ display: 'grid', gap: 4, fontSize: 12.5 }}>
          <span>Paramètres existants à rattacher</span>
          {/* Le paramètre de même code est relié d'office : le proposer encore
              laissait croire qu'il fallait l'ajouter une seconde fois. */}
          <SelecteurPastilles options={memeCode ? parametres.filter((p) => p.documentId !== memeCode.documentId) : parametres}
            choisis={analytes} onChange={setAnalytes} modifiable
            placeholder="Ajouter un paramètre — code ou nom…" ariaLabel="Rechercher un paramètre à rattacher" unite="paramètre"
            vide="Aucun pour l’instant." />
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          3 · La <strong>nature</strong> de l’examen et la façon dont l’échantillon arrive (tube primaire, matière séparée, congelée…) se déduisent ensuite des paramètres rattachés. Rien d’autre à saisir ici.
        </div>

        {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}
        <div>
          <Bouton variante="primaire" disabled={envoi || !verdict.valide || !!examenPris || !v.libelle.trim() || !v.famille} onClick={() => {
              setEnvoi(true); setErreur('');
              const corps: Record<string, unknown> = {
                ...v, delaiRenduHeures: v.delaiRenduHeures.trim() === '' ? null : Number(v.delaiRenduHeures), analyteIds: analytes,
              };
              if (!memeCode && jumeau === 'creer') corps.parametre = { ...pj, nom: pj.nom || v.libelle };
              void moteur('manuel-ref/fiches', corps).then((r) => {
                setEnvoi(false);
                if (!r.ok) { setErreur(r.message); return; }
                setResultat({
                  code: v.code, parametres: Number(r.data.parametres || 0), parametreCree: !!r.data.parametreCree,
                  materiels: Array.isArray(r.data.materielsProposes) ? (r.data.materielsProposes as string[]) : [],
                });
              });
            }}>Créer l’examen</Bouton>
        </div>
      </div>
    </Modale>
  );
}

/* ── L'aperçu public : la vitrine elle-même, pas une reconstitution ───────── */

/**
 * La fiche telle que le PUBLIC la voit — décision RQ du 18/08/2026 : « je veux
 * au moins un aperçu identique à ce que voit une personne publique et qui est
 * poussé vers la vitrine ».
 *
 * C'est la page de la vitrine, embarquée telle quelle (`/fiche/<code>`, une
 * adresse par référence — le contrat d'adressage). PAS une reconstitution :
 * un second rendu « fidèle » diverge à la première évolution de la vitrine,
 * et l'aperçu se mettrait à promettre autre chose que ce que le patient voit.
 * Embarquée, la question ne se pose pas — c'est LE même écran.
 */
function ApercuPublic({ code }: { code: string }) {
  const { manuel: MANUEL_URL } = useUrlsModules();
  /* `?apercu=public` : la vitrine saute la restauration de session et se rend
     comme pour un anonyme — sans ce paramètre, le cadre montrait la vitrine de
     la RQ connectée (barre d'édition, déconnexion), pas celle du patient. Le
     bac à sable (`sandbox`) a été essayé d'abord : il prive Next.js de son
     hydratation et rendait un cadre BLANC. */
  const adresse = `${MANUEL_URL}/fiche/${encodeURIComponent(code)}?apercu=public`;
  /* RIEN au-dessus du cadre (RQ, 19/08). Il y avait une phrase d'explication et
     un lien « Ouvrir dans la vitrine », qui ramenait au catalogue public : un
     aperçu doit montrer CE QUE VOIT LE PATIENT, et rien d'autre. Le moindre
     bouton de l'application au-dessus fausse le jugement — on ne sait plus si
     ce qu'on lit est la fiche ou son cadre. */
  return (
    <div style={{ marginTop: 10 }}>
      <iframe src={adresse} title={`Fiche publique ${code}`}
        style={{ width: '100%', height: 560, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-card)' }} />
    </div>
  );
}

/* ── Le dossier : tout ce qui est saisi, par groupe ────────────────────────── */

/**
 * Les champs qui s'écrivent DIRECTEMENT, sans passer par le circuit.
 *
 * Ce ne sont pas des valeurs que le catalogue diffuse : ce sont des propriétés
 * de structure du référentiel — comment on affiche les techniques, en combien
 * d'heures on rend, quels jours on réalise, si l'examen est un panel. Le
 * prescripteur ne les lit pas comme un engagement du laboratoire, elles n'ont
 * donc rien à faire dans un circuit de validation opposable.
 *
 * TOUT LE RESTE passe par brouillon → proposition → validation → diffusion.
 * C'est ce qui rend le catalogue opposable, et l'écran doit le DIRE : quelqu'un
 * qui modifie un délai de rendu et croit avoir changé la fiche se trompera de
 * bonne foi jusqu'à la prochaine impression du livret.
 */
/* Les trois champs « Patients » (Ines, 16-17/10/2026) s'écrivent aussi en
   direct : montrer ou non un examen au public et le texte qu'on lui adresse
   sont une décision de PUBLICATION du RQ, pas une consigne de prélèvement
   opposable. ⚠️ Même liste que `ACCEPTES` dans
   `backend/src/api/ged/controllers/manuel-referentiel.ts` (ecrireChampsFiche) :
   un champ présent d'un seul côté part au brouillon alors que le moteur
   l'attend en direct, ou l'inverse, et personne ne le voit. */
const CHAMPS_DIRECTS = ['affichageTechniques', 'delaiRenduHeures', 'joursRealisation', 'preanalytique',
  'visiblePatients', 'patientUtilite', 'patientPreparation'];

/** Les natures d'examen (07/09/2026) — la même liste fermée que le modèle. */
const NATURES_EXAMEN = [
  { code: 'primaire', libelle: 'Primaire — un ou plusieurs paramètres identifiés', aide: 'L’examen mesure : il rend toujours les paramètres rattachés plus haut. C’est la quasi-totalité du catalogue — une NFS rend toujours ses vingt paramètres.' },
  { code: 'calcule', libelle: 'Calculé — dérivé d’autres examens', aide: 'Ne se mesure pas : sa valeur se déduit d’autres analyses (DFG, LDL calculé, ratios). Il n’a pas de matériel propre — celui de ses composantes.' },
  { code: 'panel_fixe', libelle: 'Panel fixe — une liste de cibles figée', aide: 'Rend toujours la même liste de cibles, que le référentiel n’inventorie pas paramètre par paramètre (ENA, BioFire, IgE spécifiques) : elles figurent sur le compte rendu.' },
  { code: 'panel_regles', libelle: 'Panel à règles — la liste dépend d’un jeu de règles', aide: 'Ce qui est rendu dépend du contexte et d’un référentiel daté (antibiogramme → germe + CASFM). Le répertoire dit ce que le laboratoire sait tester ; le jeu de règles dit ce qu’il rend.' },
  { code: 'panel_carte', libelle: 'Panel à la carte — le prescripteur choisit', aide: 'Le prescripteur compose sa demande dans le répertoire (allergènes unitaires, par exemple).' },
];

/**
 * Modifier UN champ, là où on le lit.
 *
 * La RQ demandait « la façon la plus user friendly possible » (19/08/2026).
 * Le choix retenu : on clique la valeur dans le dossier, elle devient un champ,
 * on valide. Pas de formulaire de trente lignes à parcourir pour en changer un,
 * pas de changement d'écran, et surtout pas de doute sur ce qu'on est en train
 * de modifier — c'est la ligne qu'on avait sous les yeux.
 *
 * Les champs vides deviennent visibles dès que le dossier est modifiable, sans
 * quoi on ne pourrait jamais renseigner ce qui manque.
 */
/**
 * CRÉER une DVM depuis la fiche de l'examen (RQ, 26/08/2026).
 *
 * Jusqu'ici on ne pouvait que RATTACHER une DVM déjà créée ailleurs : il fallait
 * quitter le référentiel, aller à l'écran DVM, créer le dossier, revenir, puis
 * le rattacher. Quatre gestes pour une intention unique — et la ressaisie de
 * l'analyte, avec le risque de se tromper de code.
 *
 * ⚠️ UN SEUL DOSSIER POUR TOUS LES PARAMÈTRES (arbitrage de la RQ, 26/08/2026).
 * Un ionogramme se valide en une fois. Le dossier accueille plusieurs paramètres
 * depuis le 25/08 — et leurs techniques peuvent différer de l'un à l'autre. Un
 * dossier par paramètre aurait donné quatre dossiers à signer et à réviser
 * séparément pour un seul examen.
 *
 * Le rattachement à la fiche suit immédiatement la création : sans lui, le
 * dossier existerait sans que l'examen d'où on l'a lancé le sache.
 */
function CreerDvm({ fiche, onFait }: {
  fiche: LigneCatalogue; onFait: () => void;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [equipement, setEquipement] = useState('');
  const [portee, setPortee] = useState<'A' | 'B'>('A');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const params = fiche.analytes || [];

  /* Sans paramètre, il n'y a rien à valider : une DVM porte sur un mesurande,
     pas sur un intitulé de catalogue. On le dit plutôt que d'offrir un bouton
     qui échouerait au serveur. */
  if (!params.length) {
    return (
      <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>
        aucun paramètre rattaché : une validation de méthode n’aurait rien à porter
      </span>
    );
  }
  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)}>Créer une DVM…</Bouton>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {params.length === 1
          ? params[0].code
          : `${params.length} paramètres : ${params.map((a) => a.code).join(', ')}`}
      </span>
      <input value={equipement} onChange={(e) => setEquipement(e.currentTarget.value.toUpperCase())}
        placeholder="équipement (facultatif)" style={{ fontSize: 12.5, width: 130 }} aria-label="Code de l’équipement" />
      <select value={portee} onChange={(e) => setPortee(e.currentTarget.value as 'A' | 'B')}
        style={{ fontSize: 12.5 }} aria-label="Portée de la validation">
        <option value="A">Portée A — méthode fournisseur vérifiée</option>
        <option value="B">Portée B — méthode développée ou modifiée</option>
      </select>
      <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => {
          setEnvoi(true); setErreur('');
          /* Le PREMIER paramètre porte `analyteCode` — le serveur l'exige comme
             mesurande principal du dossier — et la liste complète part dans
             `parametres`. Le serveur n'ouvre le mode multi qu'au-delà d'un
             paramètre : envoyer la liste sur un examen simple ne change rien. */
          void moteur('dvm', {
            analyteCode: params[0].code,
            equipementCode: equipement.trim(),
            portee,
            parametres: params.map((a, i) => ({ code: a.code, ordre: i + 1 })),
          }).then((r) => {
            if (!r.ok) { setEnvoi(false); setErreur(r.message); return; }
            /* `documentId` est à la RACINE de la réponse (`{ ok, documentId, ref }`),
               pas sous une clé `dvm` — vérifié sur le contrôleur, pas supposé. */
            const id = String((r.data as { documentId?: string })?.documentId || '');
            if (!id) { setEnvoi(false); setErreur('Dossier créé, mais son identifiant n’a pas été rendu : rattachez-le à la main.'); return; }
            /* Le rattachement suit la création dans le même geste. S'il échoue,
               on le DIT : le dossier existe, et le taire laisserait croire que
               rien ne s'est passé — alors qu'un dossier orphelin a été créé. */
            void moteur(`dvm/${id}/rattacher`, { ajouterExamens: [fiche.code] }).then((r2) => {
              setEnvoi(false);
              if (!r2.ok) { setErreur(`Dossier créé, mais non rattaché : ${r2.message}`); return; }
              setOuvert(false); onFait();
            });
          });
        }}>Créer</Bouton>
      <Bouton taille="sm" disabled={envoi} onClick={() => setOuvert(false)}>Annuler</Bouton>
      {erreur && <span role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</span>}
    </span>
  );
}

function RattacherDvm({ ficheCode, onFait }: { ficheCode: string; onFait: () => void }) {
  const [ouvert, setOuvert] = useState(false);
  const [liste, setListe] = useState<{ documentId: string; ref: string; analyteCode: string }[] | null>(null);
  const [choix, setChoix] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  useEffect(() => {
    if (!ouvert || liste) return;
    void fetch('/api/ged/ged/dvm', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setListe(((d?.lignes || []) as { documentId: string; ref: string; analyteCode: string }[])
        .map((v) => ({ documentId: String(v.documentId), ref: String(v.ref), analyteCode: String(v.analyteCode || '') }))))
      .catch(() => setListe([]));
  }, [ouvert, liste]);
  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => setOuvert(true)}>Rattacher une DVM…</Bouton>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <select value={choix} onChange={(e) => setChoix(e.currentTarget.value)} style={{ fontSize: 12.5, maxWidth: 240 }}>
        <option value="">{liste ? 'choisir une DVM…' : 'chargement…'}</option>
        {(liste || []).map((v) => <option key={v.documentId} value={v.documentId}>{v.ref}{v.analyteCode ? ` · ${v.analyteCode}` : ''}</option>)}
      </select>
      <Bouton variante="primaire" taille="sm" disabled={envoi || !choix} onClick={() => {
          setEnvoi(true); setErreur('');
          void moteur(`dvm/${choix}/rattacher`, { ajouterExamens: [ficheCode] }).then((r) => {
            setEnvoi(false);
            if (!r.ok) { setErreur(r.message); return; }
            setOuvert(false); setChoix(''); onFait();
          });
        }}>Rattacher</Bouton>
      <Bouton taille="sm" disabled={envoi} onClick={() => setOuvert(false)}>Annuler</Bouton>
      {erreur && <span role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</span>}
    </span>
  );
}

type ListesDuDossier = {
  lists: Record<string, string[]>;
  enums: Record<string, { code: string; libelle: string }[]>;
} | null;

function EditionChamp({ fiche, champ, valeur, listes, opposables, onFerme, onEcrit }: {
  fiche: LigneCatalogue; champ: string; valeur: unknown; listes: ListesDuDossier;
  /** Les champs opposables, lus au moteur : décident de ce que l'éditeur annonce. */
  opposables: string[];
  onFerme: () => void; onEcrit: () => void;
}) {
  const def = FIELD_CATALOG.find((f) => f.key === champ);
  const direct = CHAMPS_DIRECTS.includes(champ);
  const opposable = opposables.includes(champ);
  const [v, setV] = useState(() => {
    if (Array.isArray(valeur)) return valeur.join(' · ');
    if (typeof valeur === 'boolean') return valeur ? 'oui' : 'non';
    return valeur === null || valeur === undefined ? '' : String(valeur);
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const champRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  useEffect(() => { champRef.current?.focus(); }, []);

  /** La valeur telle que le serveur l'attend, selon le type du catalogue. */
  const valeurEnvoyee = (): unknown => {
    const t = def?.type;
    if (champ === 'materiels') { try { return JSON.parse(v); } catch { return []; } }
    if (t === 'syn' || t === 'tags') return v.split('·').map((x) => x.trim()).filter(Boolean);
    if (t === 'list' && (champ === 'joursRealisation' || champ === 'milieux')) return v.split(/[\s·,]+/).filter(Boolean);
    if (t === 'number' || champ === 'delaiRenduHeures') return v.trim() === '' ? null : Number(v);
    return v;
  };

  const enregistrer = () => {
    setEnvoi(true); setErreur('');
    /* Le brouillon FUSIONNE au moteur (14/10/2026) : un seul champ part, les
       autres déjà en attente sont conservés. */
    const route = direct
      ? moteur(`manuel-ref/fiches/${fiche.documentId}/champs`, { [champ]: valeurEnvoyee() })
      : moteur('manuel-circuit/brouillon', { code: fiche.code, values: { [champ]: valeurEnvoyee() } });
    void route.then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      onEcrit(); onFerme();
    });
  };

  /**
   * LES OPTIONS DE CE CHAMP, s'il en a.
   *
   * Deux sources : les ÉNUMÉRATIONS du modèle (`recipient`, `lieu`) — codes
   * fermés, avec leur libellé — et les LISTES configurables du paramétrage
   * (`famille`, `transport`, les conservations, les délais…), qui
   * sont du texte libre discipliné.
   *
   * ⚠️ LA VALEUR ACTUELLE EST TOUJOURS PROPOSÉE, même absente de la liste, et
   * marquée « hors liste ». Sans cela, un `<select>` ne peut pas afficher ce
   * que la fiche porte : le navigateur retomberait sur la première option, et
   * ouvrir un champ pour le regarder l'aurait SILENCIEUSEMENT changé au premier
   * enregistrement. C'est le défaut classique de ce genre d'écran, et il ne se
   * voit qu'après coup, sur une fiche publiée.
   */
  const options: { valeur: string; libelle: string }[] | null = (() => {
    const e = listes?.enums?.[champ];
    if (e?.length) {
      const connues = e.map((x) => ({ valeur: x.code, libelle: `${x.libelle} (${x.code})` }));
      return v && !e.some((x) => x.code === v)
        ? [...connues, { valeur: v, libelle: `${v} — hors liste` }]
        : connues;
    }
    // Même garde que dans l'Avant/Après (13/09/2026) : la sous-famille ne
    // propose que celles de LA FAMILLE de cette fiche, pas l'union de toutes.
    // Cette fenêtre n'édite qu'un champ à la fois : elle n'a que la famille
    // PUBLIÉE (`fiche.famille`), pas une famille en cours de changement dans
    // le même geste — ce cas se règle depuis l'Avant/Après.
    if (champ === 'sousFamille') {
      const propre = fiche.famille ? listes?.lists?.[`sousFamille:${fiche.famille}`] : undefined;
      const l = propre?.length ? propre : listes?.lists?.sousFamille;
      return l?.length
        ? l.map((x) => ({ valeur: x, libelle: x })).concat(v && !l.includes(v) ? [{ valeur: v, libelle: `${v} — hors liste` }] : [])
        : null;
    }
    const l = listes?.lists?.[champ];
    if (l?.length) {
      const connues = l.map((x) => ({ valeur: x, libelle: x }));
      return v && !l.includes(v)
        ? [...connues, { valeur: v, libelle: `${v} — hors liste` }]
        : connues;
    }
    return null;
  })();

  const commun = {
    ref: champRef as never,
    value: v,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setV(e.currentTarget.value),
    style: { ...inputStyle, width: '100%', fontSize: 12.5 },
    // Échap referme sans écrire — on doit pouvoir se raviser d'un doigt.
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onFerme(); }
      if (e.key === 'Enter' && def?.type !== 'area' && !envoi) { e.preventDefault(); enregistrer(); }
    },
  };

  /* Rendu DANS la ligne depuis le 25/08/2026 : plus de carte ni de titre —
     l'intitulé du champ est déjà à gauche, et le répéter dans un encadré
     ferait un bloc plus haut que la rubrique qui le contient. */
  return (
    <div>
      {champ === 'milieux' ? (
        // Fermé mais MULTI-VALUE — voir la même garde dans l'Avant/Après.
        <SelecteurPastilles
          options={(listes?.enums?.milieux || []).map((o) => ({ documentId: o.code, code: o.code, nom: o.libelle }))}
          choisis={v ? v.split(/[\s·,]+/).filter(Boolean) : []}
          onChange={(codes) => setV(codes.join(' · '))}
          modifiable placeholder="Ajouter un milieu prélevé…" ariaLabel="Milieux biologiques prélevés"
          unite="milieu" mono={false}
          vide="Aucun — l’examen n’accepte encore aucun milieu déclaré." />
      ) : options ? (
        <>
          <select value={v} onChange={(e) => setV(e.currentTarget.value)}
            aria-label={def?.label || champ}
            style={{ ...inputStyle, width: '100%', fontSize: 12.5 }}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onFerme(); } }}>
            {/* Le vide est une option À PART ENTIÈRE : beaucoup de ces champs
                sont facultatifs, et sans elle on ne pourrait plus effacer une
                valeur posée par erreur. */}
            <option value="">— non renseigné —</option>
            {options.map((o) => <option key={o.valeur} value={o.valeur}>{o.libelle}</option>)}
          </select>
          {!!listes?.lists?.[champ] && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Liste réglée au Paramétrage → Listes. Une valeur qui y manque s’y ajoute.
            </div>
          )}
        </>
      ) : (def?.type === 'area' || def?.type === 'bullets' || v.length > 80) ? (
        /* Un paragraphe se saisit dans un pavé, pas dans une ligne. Le
           catalogue partagé déclare `principe` et `valeursRef` en « text »
           alors que ce sont des phrases entières : plutôt que de changer un
           contrat que trois applications se partagent, on regarde la longueur
           de ce qu'il y a réellement à écrire. Les « bullets » (une consigne
           par ligne) se saisissent de même, une ligne par puce. */
        <textarea {...commun} rows={def?.type === 'bullets' ? 7 : 4} />
      ) : (
        <input {...commun} />
      )}

      {(def?.type === 'syn' || def?.type === 'tags') && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Séparer par « · ».</div>
      )}
      {def?.type === 'bullets' && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Une consigne par ligne : le patient lit une liste d’étapes.</div>
      )}

      {/* CE QUI VA SE PASSER, écrit avant de cliquer. */}
      <div style={{ fontSize: 12.5, color: direct ? 'var(--text-muted)' : 'var(--cyan-700)', marginTop: 7, lineHeight: 1.5 }}>
        {direct
          ? 'Réglage du référentiel : appliqué immédiatement.'
          : opposable
            ? 'Champ OPPOSABLE aux préleveurs et confrères : l’enregistrement va au brouillon de cette fiche ; '
              + 'un autre RQ devra l’approuver, avec une date d’application, avant diffusion.'
            : 'Correction simple : l’enregistrement va au brouillon de cette fiche ; le RQ pourra la diffuser '
              + 'seul depuis le bandeau en tête de page, le journal l’inscrira.'}
      </div>

      {erreur && <div role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5, marginTop: 6 }}>{erreur}</div>}

      <PiedActions taille="sm" style={{ marginTop: 9 }} annuler={{ onClick: onFerme, disabled: envoi }} principale={{ libelle: direct ? 'Enregistrer' : 'Enregistrer le brouillon', disabled: envoi, onClick: enregistrer }} />
    </div>
  );
}

/**
 * Les groupes voulus par la RQ (18/08/2026) : identité, pré-analytique,
 * réalisation & rendu, tarifs & sous-traitance, accréditation — reliée aux DVM
 * quand elles existent.
 *
 * MODIFIABLE depuis le 19/08 : chaque ligne rattachée à un champ du catalogue
 * s'édite sur place (cf. `EditionChamp`). Les rubriques calculées — valeurs de
 * référence par paramètre, DVM — restent en lecture : elles ne sont pas des
 * champs de la fiche mais le reflet d'autres registres, et les modifier ici
 * ferait diverger deux vérités.
 */
/* ⚠️ L'ONGLET RÉGLAGES N'AVAIT PAS LA FORME DES AUTRES (RQ, 25/08/2026 :
   « esthétiquement les modules ne sont pas comme d'habitude »).

   Les onglets voisins — Intervalles, Critères, Performance, Interprétation —
   sont faits de CARTES : un fond, une bordure, un intitulé en petites
   capitales. Les Réglages étaient une suite plate d'intitulés et de champs,
   sans rien pour séparer « les paramètres de l'examen » de « le délai de
   rendu ». Sur un formulaire de cinq blocs, plus rien ne dit où commence une
   décision et où finit la précédente.

   `SectionReglage` reprend exactement la forme des voisins, et porte en plus
   l'aide SOUS son intitulé plutôt qu'à la fin du bloc — c'est avant de cocher
   qu'on a besoin de savoir ce qu'on coche. */
/* ── La PAGE À RUBRIQUES (essai du 31/10/2026, maquette validée par Ines) ─────
 *
 * Relevés Claude pour Chrome des 28-29/10 : deux façons d'enregistrer sur une
 * même fiche, sans dire laquelle publie ; une même information à plusieurs
 * endroits ; des sous-onglets qui décalent la mise en page et font cliquer à
 * côté. La page répond par trois règles :
 *   · une seule page qui défile, un SOMMAIRE fixe à gauche — plus d'onglets ;
 *   · chaque rubrique porte sa MARQUE — ce que fait son enregistrement — et
 *     n'a qu'une façon d'enregistrer ;
 *   · une information n'apparaît qu'une fois, chez son propriétaire.
 */
export type MarqueRubrique = 'direct' | 'relecture' | 'validation' | 'lecture';
const MARQUES: Record<MarqueRubrique, { libelle: string; ton: 'success' | 'warning' | 'info' | 'muted'; aide: string }> = {
  direct: { libelle: 'S’applique tout de suite', ton: 'success', aide: 'Le catalogue public change dès l’enregistrement, sans relecture.' },
  relecture: { libelle: 'Part en relecture', ton: 'warning', aide: 'Enregistré au brouillon : la fiche publique ne change qu’une fois la modification diffusée, depuis le bandeau en tête de page.' },
  validation: { libelle: 'Proposition à valider', ton: 'info', aide: 'Un intervalle se propose, puis se valide : il n’entre en vigueur qu’après validation.' },
  lecture: { libelle: 'Lecture', ton: 'muted', aide: 'Reflet d’autres registres : rien ne se modifie ici.' },
};
export type RubriqueDePage = {
  id: string; titre: string; groupe: string; marque: MarqueRubrique;
  aide?: React.ReactNode; compteur?: number; repliee?: boolean; contenu: React.ReactNode;
};

function DispositionPage({ prefixe, rubriques, entete }: {
  /** Préfixe des ancres — unique par examen. */
  prefixe: string; rubriques: RubriqueDePage[];
  /** Ce qui précède les rubriques, en pleine largeur de colonne (contradictions). */
  entete?: React.ReactNode;
}) {
  const [active, setActive] = useState(rubriques[0]?.id || '');
  /* La rubrique ACTIVE suit la lecture : la plus haute visible dans le tiers
     supérieur de l'écran. */
  const ids = rubriques.map((r) => r.id).join(' ');
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const ordre = ids.split(' ');
    const vues = new Map<string, boolean>();
    const obs = new IntersectionObserver((entrees) => {
      for (const e of entrees) vues.set(e.target.id, e.isIntersecting);
      const premiere = ordre.find((id) => vues.get(`${prefixe}-${id}`));
      if (premiere) setActive(premiere);
    }, { rootMargin: '-80px 0px -60% 0px' });
    for (const id of ordre) {
      const el = document.getElementById(`${prefixe}-${id}`);
      if (el) obs.observe(el);
    }
    return () => obs.disconnect();
  }, [prefixe, ids]);
  const aller = (id: string) => {
    setActive(id);
    const el = document.getElementById(`${prefixe}-${id}`);
    if (el && el.tagName === 'DETAILS') (el as HTMLDetailsElement).open = true;
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const groupes = [...new Set(rubriques.map((r) => r.groupe))];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 190px) minmax(0, 1fr)', gap: 20, alignItems: 'start', marginTop: 14 }}>
      <nav aria-label="Sommaire de l’examen" style={{ position: 'sticky', top: 72, display: 'grid', gap: 2 }}>
        {groupes.map((g) => (
          <div key={g} style={{ display: 'grid', gap: 1, marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-faint)', padding: '0 10px 3px' }}>{g}</div>
            {rubriques.filter((r) => r.groupe === g).map((r) => (
              <button key={r.id} type="button" className="ged-btn" onClick={() => aller(r.id)}
                aria-current={active === r.id ? 'true' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, textAlign: 'left',
                  padding: '5px 10px', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                  fontFamily: 'var(--font-sans)', fontSize: 13,
                  background: active === r.id ? 'var(--surface-sunken)' : 'transparent',
                  color: active === r.id ? 'var(--text-strong)' : 'var(--text-body)', fontWeight: active === r.id ? 600 : 500,
                }}>
                <span>{r.titre}</span>
                {r.compteur !== undefined && <span style={{ fontSize: 11.5, color: 'var(--text-faint)', fontVariantNumeric: 'tabular-nums' }}>{r.compteur}</span>}
              </button>
            ))}
          </div>
        ))}
      </nav>
      <div style={{ display: 'grid', gap: 12, minWidth: 0 }}>
        {entete}
        {rubriques.map((r) => {
          const m = MARQUES[r.marque];
          const tete = (
            <>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' }}>{r.titre}</h2>
              {r.compteur !== undefined && r.repliee && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{r.compteur}</span>}
              <Pastille compacte ton={m.ton} title={m.aide}>{m.libelle}</Pastille>
            </>
          );
          const corps = (
            <>
              {r.aide && <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '88ch' }}>{r.aide}</p>}
              <div style={{ marginTop: 10 }}>{r.contenu}</div>
            </>
          );
          /* `scrollMarginTop` : l'ancre ne doit pas glisser sous l'en-tête fixe. */
          return r.repliee ? (
            <details key={r.id} id={`${prefixe}-${r.id}`} style={{ ...card, padding: '12px 16px', scrollMarginTop: 72 }}>
              <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{tete}</summary>
              {corps}
            </details>
          ) : (
            <section key={r.id} id={`${prefixe}-${r.id}`} style={{ ...card, padding: '14px 16px', scrollMarginTop: 72 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>{tete}</div>
              {corps}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function SectionReglage({ titre, aide, children }: {
  titre: string; aide?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    /* Pas de `marginTop` : les sections sont désormais posées dans le
       `PanneauDossier`, dont la grille tient l'écart. Cumulés, les deux
       creusaient 26 px entre deux blocs. */
    <section style={{ ...card, padding: 14 }}>
      {/* L'intitulé de la maison — il était redessiné ici en 10,5 px, une
          demi-taille en dessous des onglets voisins. `marginBottom: 0` : l'aide
          juste dessous pose son propre écart. */}
      <TitreRubrique style={{ marginBottom: 0 }}>{titre}</TitreRubrique>
      {aide && (
        <p style={{ margin: '5px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '88ch' }}>
          {aide}
        </p>
      )}
      <div style={{ marginTop: 9 }}>{children}</div>
    </section>
  );
}

/**
 * FAIRE DESCENDRE LA TECHNIQUE SUR LE PARAMÈTRE — la reprise, avec sa lecture.
 *
 * Décision de la RQ du 26/08/2026, après lecture de ses propres données : on
 * mesure un PARAMÈTRE, pas un examen. La technique relève du triplet
 * paramètre × technique × équipement.
 *
 * ── Pourquoi un écran, et pas un script ─────────────────────────────────────
 *
 * Une reprise sans écran ne se joue qu'une fois, par celui qui l'a écrite. Or
 * celle-ci se rejouera : à chaque examen dont on arbitrera les techniques, de
 * nouveaux rattachements deviendront mécaniques. Elle doit donc être à portée
 * de la RQ, et non d'un terminal.
 *
 * ⚠️ LECTURE D'ABORD, TOUJOURS. Le premier bouton n'écrit rien : il rend ce qui
 * SERAIT posé, et surtout ce que la reprise refuse de trancher. Une reprise
 * qu'on applique sans l'avoir lue est une reprise qu'on ne relit jamais.
 */
function RepriseTechniques() {
  /** Ce que la reprise rend. Tout est facultatif : l'écran doit rester debout
   *  si le moteur enrichit sa réponse ou en omet une partie. */
  type Bilan = {
    applique?: boolean; transposes?: number; dejaFait?: number;
    aArbitrer?: { examen: string; libelle: string; parametres: string[]; techniques: string[] }[];
    panels?: string[];
  };
  const [bilan, setBilan] = useState<Bilan | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const jouer = (appliquer: boolean) => {
    setEnvoi(true); setErreur('');
    void moteur('manuel-ref/techniques/vers-parametres', { appliquer }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      setBilan(r.data as Bilan);
    });
  };

  const aArbitrer = bilan?.aArbitrer || [];
  return (
    <div style={{ ...card, padding: 14, display: 'grid', gap: 10, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
        Faire descendre la technique sur le paramètre
      </div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, maxWidth: '84ch' }}>
        On mesure un paramètre, pas un examen. Cette reprise ne traite que les examens à
        <strong> un seul paramètre</strong>, où le rattachement est mécanique. Elle <strong>ne devine
        rien</strong> sur les autres : ceux à plusieurs paramètres vous sont rendus pour arbitrage,
        et les panels non décomposés gardent leur technique sur l’examen.
        <br />
        Le lien de l’examen n’est pas retiré : le couper pendant que des examens attendent leur
        arbitrage viderait leur fiche publique.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Bouton taille="sm" disabled={envoi} onClick={() => jouer(false)}>
          {envoi ? 'Lecture…' : 'Voir ce qui serait posé'}
        </Bouton>
        {/* Appliquer n'est offert QU'APRÈS une lecture — et seulement s'il y a
            quelque chose à poser. Un bouton d'écriture disponible d'emblée
            invite à sauter la lecture, qui est tout l'intérêt du dispositif. */}
        {bilan && !bilan.applique && Number(bilan.transposes) > 0 && (
          <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={() => jouer(true)}>
            Appliquer les {String(bilan.transposes)} rattachement(s)
          </Bouton>
        )}
      </div>

      {erreur && <div role="alert" style={{ color: 'var(--red-600)', fontSize: 12.5 }}>{erreur}</div>}

      {bilan && (
        <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6 }}>
          <div>
            <strong>{String(bilan.transposes)}</strong> rattachement(s) {bilan.applique ? 'posés' : 'à poser'}
            {Number(bilan.dejaFait) > 0 && <> · {String(bilan.dejaFait)} paramètre(s) déjà à jour</>}
            {aArbitrer.length > 0 && <> · <strong>{aArbitrer.length}</strong> examen(s) à arbitrer</>}
            {(bilan.panels || []).length > 0 && <> · {(bilan.panels || []).length} panel(s) sans paramètre</>}
          </div>
          {bilan.applique && (
            <div style={{ color: 'var(--green-700)' }}>
              Appliqué — le retrait ou l’ajout est écrit au journal des techniques.
            </div>
          )}
          {!!aArbitrer.length && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                Les {aArbitrer.length} examens que la reprise refuse de trancher
              </summary>
              <div style={{ marginTop: 6, display: 'grid', gap: 3, maxHeight: 260, overflowY: 'auto' }}>
                {aArbitrer.map((x) => (
                  <div key={x.examen} style={{ fontSize: 12.5 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{x.examen}</span>
                    {' — '}{x.libelle}
                    <span style={{ color: 'var(--text-muted)' }}>
                      {' · '}{(x.parametres || []).join(', ')}
                      {' ← '}{(x.techniques || []).join(', ')}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                « Quelle technique pour quel paramètre » est un jugement : un examen qui mêle
                colorimétrie et PCR ne se répartit pas tout seul. Cela se tranche dans le dossier de
                chaque paramètre, onglet « Paramètres ».
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/** La forme AFFICHÉE d'une valeur du catalogue — la même pour une valeur
 *  publiée et pour celle d'un brouillon, sinon l'œil lit une différence là où
 *  il n'y a qu'un libellé. */
function texteDe(champ: string, v: unknown, listes: ListesDuDossier): string {
  if (v === null || v === undefined) return '';
  if (Array.isArray(v) && v.length && typeof v[0] === 'object') return `${v.length} entrée${v.length > 1 ? 's' : ''}`;
  if (Array.isArray(v)) return v.map(String).join(' · ');
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  const e = listes?.enums?.[champ]?.find((x) => x.code === String(v));
  return e ? `${e.libelle} (${e.code})` : String(v);
}

/** Les valeurs de référence EN VIGUEUR, regroupées par paramètre, une strate
 *  par ligne — `libelleIntervalle`, la même fonction que la fiche du paramètre.
 *  Posé par le dossier et par la rubrique « Valeurs de référence » de la page. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ValeursParParametre({ detail: d }: { detail: Record<string, any> }) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parParam = new Map<string, { nom: string; liste: any[] }>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  for (const i of ((d.intervalles || []) as any[])) {
    if (i.dateFin || !i.analyte) continue;
    const k = i.analyte.code as string;
    const e = parParam.get(k) || { nom: String(i.analyte.nom || ''), liste: [] };
    e.liste.push(i);
    parParam.set(k, e);
  }
  if (!parParam.size) return null;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 8 }}>
      {[...parParam.entries()].map(([code, { nom, liste }]) => (
        <span key={code} style={{ display: 'inline-flex', flexDirection: 'column', gap: 1 }}>
          <span>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{code}</span>
            {!!nom && <span style={{ color: 'var(--text-muted)' }}>{' — '}{nom}</span>}
          </span>
          {liste.map((i: typeof liste[number]) => (
            <span key={i.documentId} style={{ paddingInlineStart: 12 }}>
              {libelleIntervalle(i)}
              {/* ⚠️ L'ORIGINE SE DIT. Une borne héritée du paramètre
                  ne se corrige pas au même endroit qu'une borne
                  propre à l'examen — et rien d'autre ne le montre. */}
              {i.origine === 'parametre' && (
                <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>
                  {' · du paramètre'}
                </span>
              )}
            </span>
          ))}
        </span>
      ))}
    </span>
  );
}

/** Les paramètres de l'examen, NOMMÉS et cliquables : le clic ouvre le panneau
 *  du paramètre sans quitter l'examen (RQ, 24/08/2026). Posé par le dossier et
 *  par la rubrique « Composition » de la page. */
function PucesParametres({ fiche }: { fiche: LigneCatalogue }) {
  const ouvrirPanneau = useGed((st) => st.ouvrirPanneau);
  const chip: React.CSSProperties = {
    display: 'inline-block', fontSize: 12.5, fontFamily: 'var(--font-mono)',
    border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '1px 8px',
    marginRight: 4, marginBottom: 3, background: 'var(--surface-card)',
  };
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 5 }}>
      {fiche.analytes.map((a) => (
        <button key={a.documentId} type="button" className="ged-btn"
          title={`Ouvrir le paramètre ${a.code} sans quitter cet examen`}
          onClick={(e) => {
            /* La rubrique entière est cliquable pour l'édition en
               ligne : sans ce `stopPropagation`, ouvrir un paramètre
               ouvrirait aussi l'éditeur du champ. */
            e.stopPropagation();
            ouvrirPanneau({ type: 'parametre', valeur: a.code, origine: fiche.code });
          }}
          style={{
            ...chip, cursor: 'pointer', textAlign: 'left',
            display: 'inline-flex', alignItems: 'baseline', gap: 6,
            color: 'var(--text-body)',
          }}>
          <span>{a.nom || a.code}</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-faint)' }}>
            {a.code} {marqueVersion(a.version)}
          </span>
        </button>
      ))}
    </span>
  );
}

/**
 * LE DOSSIER — identité, réalisation & rendu, tarifs, accréditation.
 *
 * Les groupes voulus par la RQ (18/08/2026). MODIFIABLE depuis le 19/08 : chaque
 * ligne rattachée à un champ du catalogue s'édite sur place (`EditionChamp`).
 * Les rubriques calculées — valeurs de référence par paramètre, DVM — restent
 * en lecture : elles sont le reflet d'autres registres.
 *
 * Depuis le 18/10/2026 le dossier montre AUSSI ce qui attend : une valeur
 * portée par le brouillon s'affiche surlignée, la publiée barrée à côté
 * (`ValeurAvecBrouillon`). C'est l'ancien « Avant / après », ligne à ligne, à
 * l'endroit où l'on lit la valeur — et l'éditeur part de la valeur en attente,
 * pas de la publiée, sans quoi on « corrigerait » un brouillon sans le voir.
 */
function DossierExamen({ fiche, detail, dvms, estRq, onEcrit, brouillon, listes, opposables, page = false }: {
  fiche: LigneCatalogue;
  /** Posé dans la PAGE à rubriques (31/10/2026) : ce qui y a sa propre rubrique
   *  — paramètres, délai, jours, principe calculé, valeurs par paramètre — n'est
   *  pas répété ici. Une information, un endroit. */
  page?: boolean;
  /* ⚠️ `any` ASSUMÉ, et c'est la seule occurrence de ce fichier. Le détail d'un
     examen est servi par le moteur et sa FORME DÉPEND DU PARAMÉTRAGE : les
     champs de fiche sont configurables (`field-config`), et le laboratoire en
     ajoute. Aucune interface écrite ici ne les décrirait durablement — elle
     mentirait dès le premier champ ajouté, ce qui est pire qu'un type large.
     `unknown` dirait la vérité mais imposerait un cast à chacune des trente
     lectures : trente occasions de se tromper, pour zéro garantie gagnée. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  detail: Record<string, any>;
  dvms: { ref: string; version: number | null; analyteCode: string; equipementCode: string | null; portee: string | null; statut: string | null; docCode: string | null }[];
  estRq: boolean; onEcrit: () => void;
  /** Ce qui attend dans le circuit sur cet examen — lu par la page, `null` si rien. */
  brouillon: BrouillonFiche | null;
  /** Les listes déroulantes et énumérations, chargées une fois par la page. */
  listes: ListesDuDossier;
  /** Les champs opposables, lus au moteur. */
  opposables: string[];
}) {
  const d = detail;
  /* Le panneau des fiches liées : ouvrir un paramètre sans perdre sa page. */
  const ouvrirPanneau = useGed((st) => st.ouvrirPanneau);
  const [champ, setChamp] = useState<string | null>(null);
  /* L'éditeur descend DANS la ligne (25/08/2026) : il s'affichait en tête du
     dossier, ce qui obligeait à remonter pour saisir puis redescendre pour
     vérifier — et sur quarante lignes, à perdre de vue ce qu'on changeait. */
  const editable = estRq ? {
    onEditer: setChamp,
    champEnCours: champ,
    editeur: (c: string) => (
      /* L'éditeur part de la valeur EN ATTENTE s'il y en a une : c'est elle
         qu'on corrige, le brouillon fusionnant au moteur. */
      <EditionChamp fiche={fiche} champ={c} valeur={brouillon && c in brouillon.values ? brouillon.values[c] : d[c]} listes={listes}
        opposables={opposables} onFerme={() => setChamp(null)} onEcrit={onEcrit} />
    ),
  } : {};
  /** La valeur d'une ligne : la publiée, ou — si le brouillon la remplace — les
   *  deux, l'ancienne barrée et la nouvelle surlignée. */
  const val = (c: string, publie: React.ReactNode) => (brouillon && c in brouillon.values
    ? <ValeurAvecBrouillon publie={publie} brouillon={texteDe(c, brouillon.values[c], listes)} /> : publie);
  /** Une valeur qui ne se modifie PAS ici, suivie de l'endroit où elle se règle
   *  (pour l'éditeur seulement — le lecteur n'en a pas l'usage). */
  const ailleurs = (v: React.ReactNode, ou: string) => (estRq && v
    ? <>{v} <span style={{ color: 'var(--text-faint)', fontSize: 11.5 }}>· {ou}</span></> : v);
  const chip: React.CSSProperties = {
    display: 'inline-block', fontSize: 12.5, fontFamily: 'var(--font-mono)',
    border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '1px 8px',
    marginRight: 4, marginBottom: 3, background: 'var(--surface-card)',
  };
  return (
    <div>
      {estRq && !champ && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8 }}>
          {/* Toutes les lignes ne sont pas cliquables : celles qui se règlent
              ailleurs le DISENT (relevé du 29/10/2026 : « Cliquez une ligne »
              était faux pour le délai, deux clics perdus). */}
          Cliquez une ligne pour la modifier ; celles qui se règlent ailleurs l’indiquent.
        </div>
      )}
      {/* Un examen sans mesurande ni nature de panel : la fiche publique
          n'annonce rien. La nature se règle dans l'onglet Dossier. */}
      {estRq && !fiche.analytes.length && !String(fiche.nature || '').startsWith('panel') && fiche.nature !== 'calcule' && (
        <div style={{ fontSize: 12.5, marginTop: 8, padding: '8px 10px', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
          Aucun mesurande, et nature « primaire » : la fiche publique n’annonce rien. Rattachez ses paramètres,
          ou déclarez-le <strong>panel</strong> dans les réglages de structure, plus bas.
        </div>
      )}
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: '0 24px', marginTop: 10 }}>
      <div>
        <Rubrique {...editable} titre="Identité" lignes={[
          ['Libellé', val('libelle', d.libelle), 'libelle'],
          ['Famille', [d.famille, d.sousFamille].filter(Boolean).join(' › ')],
          ['Synonymes', val('synonymes', Array.isArray(d.synonymes) && d.synonymes.length ? d.synonymes.join(' · ') : null), 'synonymes'],
          ['Cibles recherchées', val('cibles', Array.isArray(d.cibles) && d.cibles.length ? d.cibles.join(' · ') : null), 'cibles'],
          ['Version', libelleComposition({ code: d.code, version: d.version }, fiche.analytes)],
          /* LES PARAMÈTRES SONT CLIQUABLES, et nommés (RQ, 24/08/2026 : « je
             voudrais que dans l'examen, la liste des paramètres reliés soit
             plus explicite, et pouvoir cliquer sur le paramètre »).

             Une suite de codes — `ALAT`, `ASAT` — ne dit rien à qui ne les
             connaît pas déjà, et c'est justement la personne qui a besoin de
             les lire. Le NOM passe donc devant, le code reste à côté pour qui
             le cherche.

             Le clic ouvre le PANNEAU des fiches liées, pas une navigation :
             on ne quitte pas l'examen qu'on est en train de lire. */
          ...(page ? [] : [
            ['Mesurande ou analyte', fiche.analytes.length
            ? (
              <PucesParametres fiche={fiche} />
            )
            : (String(d.nature || '').startsWith('panel') ? `${NATURES_EXAMEN.find((n) => n.code === d.nature)?.libelle || d.nature}${d.jeuDeRegles ? ` — jeu ${d.jeuDeRegles}` : ''}` : null)],
          ] as LigneRubrique[]),
        ]} />
        {/* La rubrique « Pré-analytique » (douze lignes à plat) et « Matières
            biologiques » sont REMPLACÉES par « Prélèvement & réception »
            (RQ, 14/09/2026 : « le dossier est devenu incompréhensible ») —
            posé en pleine largeur, sous la grille. */}
      </div>
      <div>
        <Rubrique {...editable} titre={page ? 'Textes du catalogue' : 'Réalisation & rendu'} lignes={[
          /* ⚠️ Traduction retirée du composant (26/08/2026). Elle ne couvrait
             que `place` : les examens SOUS-TRAITÉS affichaient « st ». Le
             libellé vient maintenant du moteur, qui a la table entière — c'est
             ce qui faisait dire « Sur place » ici et « place » au tableau à
             plat, sans qu'aucun des deux soit faux localement. */
          ['Lieu', d.lieuLibelle || d.lieu],
          /* Le PRINCIPE DE MÉTHODE : calculé depuis les techniques et leurs
             automates, donc en LECTURE — le modifier ici ferait diverger deux
             vérités, et c'est exactement ce que les techniques concaténées
             avaient produit. Il se corrige en changeant la technique. */
          /* Dans la page, ces trois lignes ont leur rubrique « Réalisation & délai ». */
          ...(page ? [] : [
            ['Principe de méthode', ailleurs((d.principeMethode as string[] | undefined)?.join(' · ') || '', 'déduit des techniques — onglet Qualité')],
            ['Délai de rendu', ailleurs(d.delaiRenduHeures ? formaterDelai(d.delaiRenduHeures, 'long') : null, 'se règle plus bas, dans les réglages de structure')],
            ['Jours de réalisation', ailleurs(formaterJours(d.joursRealisation), 'se règlent plus bas, dans les réglages de structure')],
          ] as LigneRubrique[]),
          /* Dans la page, ces deux textes saisis à la main SE DISENT tels : ils
             doublent des données structurées et seront remplacés par elles
             (décision d'Ines, 30/10/2026 : « rien à la main »). */
          [page ? 'Valeurs de référence (texte libre)' : 'Valeurs de référence', val('valeursRef', d.valeursRef), 'valeursRef'],
          [page ? 'Principe (texte libre)' : 'Principe de la méthode', val('principe', d.principe), 'principe'],
          ['Résultat calculé', d.nature === 'calcule' ? (d.formule || 'oui') : null],
          ['Indications', val('indications', d.indications), 'indications'],
        ]} />
        <Rubrique {...editable} titre="Tarifs & sous-traitance" lignes={[
          ['Cotation', val('cotation', d.cotation), 'cotation'],
          ['Code CNAM / HN', d.cnam && d.cnam !== '—' ? d.cnam : null],
          ['Proposé à la sous-traitance', val('sousTraitance', d.sousTraitance), 'sousTraitance'],
          ['Prix HT', d.prixST !== null && d.prixST !== undefined ? `${d.prixST} TND` : null],
          ['Remarque importante', d.remarqueImportante
            ? <span style={{ color: 'var(--red-700)', fontWeight: 600 }}>{d.remarqueImportante}</span> : null],
        ]} />
        {/* Les valeurs de référence, REGROUPÉES PAR PARAMÈTRE (RQ, 18/08 :
            « c'est juste au niveau de l'affichage de l'examen qu'il faut les
            présenter ensemble »). Le texte d'ensemble de la fiche reste
            au-dessus — c'est ce que le public lit ; ceci est la même chose,
            lisible ligne à ligne.

            ── ⚠️ LES STRATES, EN ENTIER (RQ, 30/08/2026) ────────────────────

            « Dans l'examen, je veux voir les strates des intervalles provenant
            de la section intervalle directement. »

            Cette rubrique tronquait la strate : elle ne rendait que le SEXE et
            les bornes, et jetait l'âge, la grossesse, la condition et la nature.
            Un paramètre à cinq tranches d'âge s'affichait donc en cinq bornes
            d'affilée, séparées par des points-virgules, sans rien pour dire à
            qui chacune s'applique — la seule information qui décide de la
            lecture d'un résultat.

            ⚠️ On appelle maintenant `libelleIntervalle`, LA MÊME fonction que
            la fiche du paramètre. Deux rendus écrits séparément finissent par ne
            plus dire la même chose de la même borne, et aucun des deux n'a tort
            localement — c'est le défaut qu'on a déjà payé sur les libellés de
            lieu et de contenant. Elle sait aussi qu'un `texteLibre` porte DÉJÀ
            sa strate en toutes lettres et ne la répète pas.

            ⚠️ Une strate par LIGNE, plus des points-virgules : « H · 3–8 ans »
            et « 2,0 – 5,0 » collés sur une même ligne se lisent comme une seule
            phrase, et l'œil rattache la mauvaise borne à la mauvaise tranche. */}
        {!page && <Rubrique titre="Valeurs de référence par paramètre" lignes={[['', <ValeursParParametre key="v" detail={d} />]]} />}
        <Rubrique {...editable} titre="Accréditation" lignes={[
          ['Accréditée', val('accredite', d.accredite), 'accredite'],
          ['Portée', val('porteeAccred', d.porteeAccred), 'porteeAccred'],
          ['Date', val('dateAccred', d.dateAccred), 'dateAccred'],
          /* Le lien voulu : « l'accréditation relierait les DVM si elles
             existent ». Aucune DVM n'est un fait, pas une case vide — on
             l'écrit. */
          /* `key` : cette paire est un COUPLE (libellé, contenu), pas une liste
             — mais la règle ne peut pas le savoir, et un avertissement qu'on
             laisse traîner finit par en couvrir un vrai. */
          ['Validations de méthode', (
            <span key="dvm" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
              {dvms.length
                ? dvms.map((v) => (
                  <span key={v.ref} style={chip} title={[v.equipementCode, v.portee, v.statut].filter(Boolean).join(' · ')}>
                    {v.ref} {marqueVersion(v.version)} · {v.analyteCode}
                  </span>
                ))
                : <span style={{ color: 'var(--text-muted)' }}>aucune DVM ne cite cet examen ni ses paramètres</span>}
              {/* RATTACHER une DVM existante (RQ, 19/08/2026) : additif côté
                  serveur — la couverture déjà posée sur la DVM n'est pas
                  emportée. Une DVM peut couvrir plusieurs paramètres. */}
              {estRq && <CreerDvm fiche={fiche} onFait={onEcrit} />}
              {estRq && <RattacherDvm ficheCode={fiche.code} onFait={onEcrit} />}
            </span>
          )],
        ]} />
      </div>
    </div>
    </div>
  );
}

/* ── L'écran ───────────────────────────────────────────────────────────────── */

export function ManuelReferentiel() {
  const estRq = useGed((s) => s.session.roles.includes('rq'));
  /* Refonte du 18/08/2026 — la RQ : « il y a trop de listes différentes et je
     me perds un peu ». TROIS entrées principales, une par nature d'objet :
     EXAMENS (ce que le prescripteur commande), PARAMÈTRES (ce que le
     laboratoire mesure), TECHNIQUES (les savoir-faire). Les registres annexes
     (combos, critères) se rangent derrière une entrée secondaire, et l'onglet
     « Arbitrage paramètres » a disparu : c'était l'outil d'appariement d'une
     reprise, close depuis — les compositions sont figées dans des fichiers
     arbitrés, plus dans un score. */
  type OngletRef = 'catalogue' | 'parametres' | 'techniques' | 'interpretations' | 'circuit' | 'parametrage';
  const ONGLETS_REF: OngletRef[] = ['catalogue', 'parametres', 'techniques', 'interpretations', 'circuit', 'parametrage'];
  /* L'ONGLET PEUT ÊTRE DEMANDÉ PAR L'URL (RQ, 24/08/2026 : « le lien de
     versions dans le catalogue 3001 n'emmène nulle part »).

     Le lien existait et redirigeait bien vers Qualité — mais il atterrissait
     sur « Examens », l'onglet par défaut, à trois clics des versions. Elles
     n'avaient pas disparu : elles sont sous « Circuit », où la vitrine les a
     laissées le 17/08. Un lien qui arrive sur la bonne page mais au mauvais
     endroit se lit exactement comme un lien mort.

     ⚠️ Lu UNE SEULE FOIS, à l'ouverture. Le relire à chaque rendu ramènerait
     l'utilisateur de force sur l'onglet demandé dès qu'il en change — le
     défaut exact trouvé le 24/08 sur la fenêtre de paramétrage.

     ⚠️ La valeur est VALIDÉE contre la liste : un `?onglet=` inventé ouvrirait
     sinon un écran vide, sans que rien ne dise pourquoi. */
  const [onglet, setOnglet] = useState<OngletRef>(() => {
    if (typeof window === 'undefined') return 'catalogue';
    const voulu = new URLSearchParams(window.location.search).get('onglet');
    return (ONGLETS_REF as string[]).includes(voulu || '') ? (voulu as OngletRef) : 'catalogue';
  });
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(onglet);
  const [lignes, setLignes] = useState<LigneCatalogue[] | null>(null);
  const [parametres, setParametres] = useState<Parametre[] | null>(null);
  /* L3 : la liste des paramètres a SON état d'erreur — avant, un échec de
     `manuel-ref/parametres` laissait l'onglet sur un squelette pour toujours. */
  const [erreurParametres, setErreurParametres] = useState('');
  /* Les listes déroulantes et leurs « i », servis par le moteur avec les
     paramètres eux-mêmes — jamais recopiés ici. */
  const [vocabulaire, setVocabulaire] = useState<ChampVoc[]>([]);
  /* Les unités, avec leur grandeur : c'est elle qui dit si une conversion a un
     sens, et l'écran doit pouvoir le montrer au moment du choix. */
  const [unites, setUnites] = useState<UniteVoc[]>([]);
  /* Ce que le registre des critères de performance ne peut rattacher à AUCUN
   * paramètre du référentiel (05/09/2026) — nommé, pour ne pas se relire
   * silencieusement « aucun critère publié » sur un paramètre qui en a un,
   * juste sous un code faux (cas trouvé : Glucose publié en « GLU », le
   * référentiel porte « GLY »). */
  const [criteresOrphelins, setCriteresOrphelins] = useState<{ libelle: string; source: string; motif: string }[]>([]);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('');
  const [ouverte, setOuverte] = useState<string | null>(null);
  // Ouvrir ou fermer une fiche est aussi une arrivée : la page commence en haut.
  useRemonteEnHaut(ouverte);
  /** Un lien `/manuel-prelevement/<code>` qui ne désigne aucun examen (17/10/2026). */
  const [erreurLienExamen, setErreurLienExamen] = useState('');
  const [parametreOuvert, setParametreOuvert] = useState<string | null>(null);
  const [filtreTechniques, setFiltreTechniques] = useState('');
  const [creationExamen, setCreationExamen] = useState(false);
  const [creationParametre, setCreationParametre] = useState(false);
  /* ── LA VUE À PLAT (RQ, 26/08/2026) ──────────────────────────────────────
   * « Voir tous mes examens dans un grand tableau avec toutes les colonnes. »
   * Elle répond aux questions qui se lisent d'un balayage vertical — quels
   * examens n'ont pas de délai, lesquels sont sous-traités sans prix — et
   * qu'aucun panneau de détail ne peut servir.
   *
   * Les 29 champs supplémentaires ne sont demandés QU'ICI : les charger dans
   * la liste ordinaire ferait payer, à chaque écriture d'un sous-onglet, une
   * vue dont on se sert une fois par mois. */
  const [vuePlat, setVuePlat] = useState(false);
  const [platCharge, setPlatCharge] = useState(false);

  /* L'ÉTAT DU CIRCUIT PAR EXAMEN (Ines, 17/10/2026 : « on ne sait pas où en est
     une fiche »). Lu avec le catalogue, affiché en pastille dans la liste :
     brouillon · à relire (proposé) · à diffuser (approuvé) · refusé. Sans
     cela, un brouillon en attente ne se voyait qu'en ouvrant la fiche, ou
     dans l'onglet Circuit, à trois clics. */
  const [circuitParCode, setCircuitParCode] = useState<Record<string, StatutCircuit>>({});
  const charger = (plat = platCharge) => {
    void moteur('manuel-circuit/etat').then((r) => {
      if (!r.ok) return; // la liste se lit sans lui ; la pastille manque, rien d'autre
      const props = (r.data.propositions as { code: string; statut: StatutCircuit }[]) || [];
      setCircuitParCode(Object.fromEntries(props.map((p) => [p.code, p.statut])));
    });
    void moteur(`manuel-ref/catalogue${plat ? '?plat=1' : ''}`).then((r) => {
      if (!r.ok) { setErreur(r.message); return; }
      setLignes((r.data.fiches as LigneCatalogue[]) || []);
      if (plat) setPlatCharge(true);
    });
    void moteur('manuel-ref/parametres').then((r) => {
      if (!r.ok) { setErreurParametres(r.message); return; }
      setErreurParametres('');
      setParametres((r.data.parametres as Parametre[]) || []);
      /* ⚠️ Le vocabulaire arrive DANS LA MÊME RÉPONSE que les paramètres. Deux
         appels auraient donné deux instants : un écran qui affiche la liste
         d'hier sur les données d'aujourd'hui propose une option que la route
         refuse, sans que rien ne l'explique. */
      setVocabulaire((r.data.vocabulaire as ChampVoc[]) || []);
      setUnites((r.data.unites as UniteVoc[]) || []);
      setCriteresOrphelins((r.data.criteresOrphelins as { libelle: string; source: string; motif: string }[]) || []);
    });
  };
  useEffect(() => { charger(); }, []);

  /* Le sélecteur de paramètres se sert de la liste DÉJÀ chargée par
     `manuel-ref/parametres`, qui pagine côté serveur et rend donc tout.
     Il lisait auparavant `ged-analytes?pagination[pageSize]=100` en direct :
     Strapi plafonne à 100 quoi qu'on demande, si bien qu'au 100ᵉ paramètre le
     suivant aurait disparu des menus SANS erreur — introuvable à l'écran, et
     invisible aux tests. Il y en a 99 ce jour. */
  const analytes = useMemo<AnalyteRef[]>(
    () => (parametres || []).filter((p) => !p.archive)
      .map((p) => ({ documentId: p.documentId, code: p.code, nom: p.nom })),
    [parametres],
  );

  /* Navigation CROISÉE : depuis un paramètre on saute à son examen, depuis un
     examen à sa technique — on suit un lien, on ne change pas de liste. */
  const ouvrirExamen = (code: string) => {
    const f = (lignes || []).find((x) => x.code === code);
    if (!f) return;
    setOnglet('catalogue'); setOuverte(f.documentId);
  };
  const ouvrirTechnique = (code: string) => {
    setOnglet('techniques'); setFiltreTechniques(code);
  };
  const ouvrirParametre = (code: string) => {
    setOnglet('parametres'); setParametreOuvert(code);
  };

  /* Ouverture demandée depuis AILLEURS — aujourd'hui la recherche globale
     (Ctrl+K). L'écran ne se contente pas d'arriver : il ouvre la bonne liste
     ET la bonne fiche, sinon on retomberait sur un catalogue de 300 lignes
     après avoir désigné précisément ce qu'on cherchait.

     La demande est CONSOMMÉE (remise à null) : sans cela, tout retour ultérieur
     sur l'écran rouvrirait la dernière fiche cherchée, y compris des jours plus
     tard — l'écran paraîtrait refuser de s'ouvrir sur sa liste. */
  const { selectedRef, select } = useGed();
  useEffect(() => {
    if (!selectedRef) return;
    const [nature, ...reste] = selectedRef.split(':');
    /* ⚠️ Un code SEUL est un examen : son code est la NATURE elle-même. La garde
       « code vide » (20/08) précédait la prise en charge du code seul (17/10) et
       l'effaçait — toute fiche d'examen ouverte se refermait aussitôt (relevé
       du 28/10/2026 : « la fiche examen ne s'ouvre pas, par aucun chemin »). */
    const code = reste.length === 0 ? nature : reste.join(':');
    if (!code) { select('selectedRef', null); return; }
    /* Un code SEUL (depuis l'adresse `/manuel-prelevement/GLY`, 17/10/2026)
       est un examen : la table d'adressage le pose tel quel dans `selectedRef`. */
    if (nature === 'examen' || reste.length === 0) {
      const codeExamen = reste.length === 0 ? nature : code;
      // Le catalogue peut n'être pas encore chargé : on attend la liste, sans
      // consommer la demande, plutôt que d'ouvrir l'onglet à vide.
    /* ⚠️ FAUX POSITIF ASSUMÉ (02/09/2026). Ce n'est pas un état ajusté à une
       entrée : c'est la CONSOMMATION d'une demande arrivée de l'extérieur — un
       lien profond posé par un autre écran. Un effet est le bon endroit pour
       réagir à un système externe, et la demande est remise à `null` juste
       après pour ne pas se rejouer. La dériver n'a pas de sens : elle n'est pas
       une fonction de l'état, elle est un événement. */
    // eslint-disable-next-line react-hooks/set-state-in-effect
      if (!lignes) { setOnglet('catalogue'); return; }
      const f = lignes.find((x) => x.code === codeExamen);
      /* Un examen introuvable (lien périmé, code mal copié) se DIT : l'adresse
         redevient celle de la liste et le message explique, au lieu d'une
         liste de 300 lignes qui semble ignorer le lien. */
      if (!f) {
        select('selectedRef', null);
        setErreurLienExamen(`« ${codeExamen} » ne correspond à aucun examen du catalogue : le lien est peut-être périmé, ou le code mal copié.`);
        return;
      }
      setOnglet('catalogue'); setOuverte(f.documentId);
      /* La demande n'est PAS remise à null pour un examen : `selectedRef` porte
         désormais le code ouvert, et c'est lui que l'adresse affiche
         (`/manuel-prelevement/GLY`). Elle est normalisée au code seul. */
      if (selectedRef !== f.code) select('selectedRef', f.code);
      return;
    }
    if (nature === 'parametre') ouvrirParametre(code);
    else if (nature === 'technique') ouvrirTechnique(code);
    select('selectedRef', null);
  }, [selectedRef, lignes]);

  /* L'ADRESSE SUIT LA FICHE (17/10/2026). Ouvrir une fiche depuis la liste pose
     son code dans `selectedRef` ; la fermer le retire ; « Précédent » (qui vide
     `selectedRef` par l'adresse) referme la fiche. Les deux effets convergent :
     ouvrir → code → (déjà ouverte) rien ; fermer → null → (déjà fermée) rien. */
  useEffect(() => {
    const code = ouverte ? (lignes || []).find((f) => f.documentId === ouverte)?.code || null : null;
    if (code && selectedRef !== code) { select('selectedRef', code); return; }
    if (!code && selectedRef && !selectedRef.includes(':')) {
      /* Un code seul en attente alors que rien n'est ouvert : c'est une demande
         d'ouverture, traitée par l'effet précédent — ne pas l'effacer. */
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouverte]);
  useEffect(() => {
    // L'adresse a vidé la sélection (« Précédent ») : la fiche se referme.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!selectedRef && ouverte) setOuverte(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRef]);

  const visibles = useMemo(() => {
    const q = filtre.trim().toLowerCase();
    return (lignes || []).filter((f) => !f.archivee
      && correspond({ ...f, lies: f.analytes }, q))
      // Le code exact d'abord (`pertinence`) ; `sort` est stable, l'ordre du catalogue tient à rang égal.
      .sort((a, b) => pertinence({ ...a, lies: a.analytes }, q) - pertinence({ ...b, lies: b.analytes }, q));
  }, [lignes, filtre]);

  const ficheOuverte = ouverte ? (lignes || []).find((f) => f.documentId === ouverte) || null : null;

  /* LA FICHE EST UNE PAGE (18/10/2026) : elle REMPLACE la liste au lieu de
     s'ouvrir par-dessus. La liste, ses filtres et son défilement restent dans
     l'état de cet écran et se retrouvent au retour. */
  if (ficheOuverte) {
    return (
      <PageExamen
        key={ficheOuverte.documentId}
        fiche={ficheOuverte} analytes={analytes} estRq={estRq}
        catalogue={lignes || []}
        onFerme={() => setOuverte(null)}
        onEcrit={() => charger()}
      />
    );
  }

  return (
    <div>
      <TitrePage
        eyebrow="Catalogue des analyses"
        titre="Référentiel des analyses"
        sousTitre="Le catalogue se paramètre ici — la vitrine du manuel ne fait plus qu’afficher."
        actions={(
          <div>
            {/* Trois entrées, une par nature d'objet — puis le circuit (qui
                engage le document opposable), les registres annexes et le
                paramétrage. */}
            <Segment ariaLabel="Rubrique du référentiel" valeur={onglet} onChange={(v) => setOnglet(v as OngletRef)}
              options={([['catalogue', `Examens (${(lignes || []).filter((f) => !f.archivee).length})`],
              ['parametres', `Paramètres (${(parametres || []).filter((x) => !x.archive).length})`],
              ['techniques', 'Techniques'],
              /* « Autres registres » retiré le 19/08 : il ne contenait plus
                 que deux choses — les combos, ZÉRO enregistrement depuis leur
                 création (l'écran a toujours été vide), et les critères
                 d'échantillon, qui sont un réglage du catalogue et ont rejoint
                 « Paramétrage ». Un onglet qui n'ouvre sur rien coûte plus
                 qu'il ne rend : c'est une liste de plus où se perdre. */
              /* L'INTERPRÉTATION BIOLOGIQUE appartient au référentiel : elle
                 décrit ce que les paramètres veulent dire ENSEMBLE. Elle se
                 range après « Techniques » — après ce qu'on mesure et comment,
                 avant le circuit qui les publie. */
              ['interpretations', 'Interprétation'],
              ['circuit', 'Circuit'], ['parametrage', 'Paramétrage']] as const).map(([valeur, l]) => ({
                valeur,
                /* La roue sur « Paramétrage », comme partout : elle dit
                   « réglage » d'un coup d'œil (décision du 24/08/2026). */
                libelle: valeur === 'parametrage' ? <><I n="gear" s={12} /> {l}</> : l,
              }))} />
          </div>
        )}
      />

      {/* Les trois registres voisins — critères d'acceptation, intervalles,
          performance — s'atteignent d'ici (18/10/2026) : un seul menu. */}
      <RegistresLies actif="manuel" />

      {/* La recherche TRANSVERSALE : un seul champ, qui dit où se trouve ce
          qu'on cherche AVANT d'avoir à choisir une liste — c'était le reproche
          central : « je me perds, trop de listes différentes ». */}
      {(onglet === 'catalogue' || onglet === 'parametres') && (
        /* Barre d'outils du tableau ouvert : la recherche à gauche, le bouton
           de création à l'extrême droite, sur la même ligne — la place est la
           même d'un onglet à l'autre, seul le libellé suit la liste. */
        <div style={{ margin: '4px 0 8px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <ChampRecherche valeur={filtre} onChange={setFiltre}
            placeholder="Rechercher — code, libellé, famille, paramètre…"
            style={{ fontSize: 12.5, maxWidth: 420, display: 'inline-block', verticalAlign: 'middle' }} />
          {filtre.trim() && (() => {
            const q = filtre.trim().toLowerCase();
            const nEx = (lignes || []).filter((f) => !f.archivee
              && correspond({ ...f, lies: f.analytes }, q)).length;
            const nPa = (parametres || []).filter((x) => !x.archive
              && correspond({ ...x, lies: x.examens }, q)).length;
            /* Ce sélecteur CHANGE D'ONGLET (Examens ↔ Paramètres) : le dire
               devant, sans quoi cliquer « 3 paramètres » basculait tout l'écran
               sans prévenir (relevé du 29/10/2026). */
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginInlineStart: 10 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Résultats dans l’onglet</span>
              <Segment ariaLabel="Afficher les résultats dans l’onglet" valeur={onglet} onChange={setOnglet}
                options={[
                  { valeur: 'catalogue' as const, libelle: `${nEx} examen${nEx > 1 ? 's' : ''}` },
                  { valeur: 'parametres' as const, libelle: `${nPa} paramètre${nPa > 1 ? 's' : ''}` },
                ]} />
              </span>
            );
          })()}
          {/* Deux lectures d'une même liste, pas deux listes : la recherche,
              le filtre et le clic-pour-ouvrir sont partagés. */}
          {/* Deux lectures, un SÉLECTEUR qui montre celle qui est affichée
              (28/10/2026 : le bouton portait le nom de l'AUTRE vue, « Liste »
              une fois à plat — on lisait l'inverse de ce qu'on voyait). */}
          {onglet === 'catalogue' && (
            <Segment ariaLabel="Présentation des examens" valeur={vuePlat ? 'plat' : 'liste'}
              onChange={(v) => {
                const suivant = v === 'plat';
                if (suivant === vuePlat) return;
                setVuePlat(suivant);
                // Les colonnes larges ne sont demandées qu'au premier passage.
                if (suivant && !platCharge) charger(true);
              }}
              options={[
                { valeur: 'liste' as const, libelle: <><I n="list" s={12} /> Liste</> },
                { valeur: 'plat' as const, libelle: <span title="Tous les examens, toutes leurs colonnes — et l’export CSV" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><I n="table" s={12} /> Vue à plat</span> },
              ]} />
          )}
          {estRq && (onglet === 'catalogue'
            ? <BoutonCreer onClick={() => setCreationExamen(true)}>Nouvel examen</BoutonCreer>
            : <BoutonCreer onClick={() => setCreationParametre(true)}>Nouveau paramètre</BoutonCreer>)}
        </div>
      )}

      {onglet === 'circuit' && <OngletCircuit estRq={estRq} />}
      {onglet === 'interpretations' && <OngletInterpretations estRq={estRq} />}
      {onglet === 'parametrage' && <OngletParametrage estRq={estRq} />}
      {onglet === 'parametres' && (
        <OngletParametres filtre={filtre} ouvert={parametreOuvert} setOuvert={setParametreOuvert}
          parametres={parametres} erreur={erreurParametres} onReessayer={() => charger()}
          ouvrirExamen={ouvrirExamen} ouvrirTechnique={ouvrirTechnique}
          estRq={estRq} onCree={charger} vocabulaire={vocabulaire} unites={unites}
          criteresOrphelins={criteresOrphelins}
          creation={creationParametre} setCreation={setCreationParametre}
          examens={(lignes || []).filter((f) => !f.archivee).map((f) => ({
            documentId: f.documentId, code: f.code, libelle: f.libelle,
            analyteIds: f.analytes.map((a) => a.documentId),
          }))} />
      )}
      {creationExamen && (
        <FormExamen onFerme={() => setCreationExamen(false)} catalogue={lignes || []}
          onCree={(code) => { setCreationExamen(false); charger(); setFiltre(code); }} />
      )}
      {onglet === 'techniques' && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        {/* Ce qui est tracé se VOIT (24/08/2026) : techniques et compositions
            s'écrivaient sous « MAN-TECHNIQUES » / « MAN-COMBOS » sans accès. */}
        <BoutonJournal reference="MAN-TECHNIQUES" quoi="des techniques" />
        <BoutonJournal reference="MAN-COMBOS" quoi="des compositions" />
      </div>}
      {onglet === 'techniques' && estRq && <RepriseTechniques />}
      {onglet === 'techniques' && <OngletTechniquesRegistre estRq={estRq} filtreInitial={filtreTechniques} ouvrirExamen={ouvrirExamen} />}
      {/* Les registres ANNEXES — réels mais secondaires : les garder en
          onglets de premier rang noyait les trois entrées principales. */}

      {onglet === 'catalogue' && (
        <>
          {erreur && <EmptyState titre="Catalogue indisponible" indice={erreur} action={<Bouton taille="sm" type="button" onClick={() => { setErreur(''); charger(); }}>Réessayer</Bouton>} />}
          {!erreur && !lignes && <Skeleton rows={8} />}
          {!erreur && lignes && (
            <>
              {vuePlat && (
                <CataloguePlat lignes={lignes || []} filtre={filtre} onOuvrir={setOuverte} />
              )}
              {erreurLienExamen && (
                <div role="alert" style={{ marginBottom: 10, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', color: 'var(--amber-700)', fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                  <span>{erreurLienExamen}</span>
                  <Bouton taille="sm" variante="discret" onClick={() => setErreurLienExamen('')}>Fermer</Bouton>
                </div>
              )}
              {!vuePlat && (
              <div style={{ ...card, padding: 0 }}>
                <Tableau
                  defilementHorizontal
                  id="catalogue-examens"
                  ajusterLargeur
                  /* L3 (10/10/2026) : niveau AVANCÉ — « Code » épinglé et
                     obligatoire, filtres par colonne, 616 lignes VIRTUALISÉES
                     (au-delà de 200, le socle ne rend que l'écran). L'export de
                     référence est celui de la vue à plat (`CataloguePlat`) :
                     un seul export par registre. */
                  niveau="avance"
                  vide={{ titre: 'Aucun examen ne répond au filtre' }}
                  lignes={visibles}
                  cleLigne={(f) => f.documentId}
                  ligneProps={(f) => ({ onClick: () => setOuverte(f.documentId), style: { cursor: 'pointer' } })}
                  colonnes={[
                    /* Plus de colonne de cases (RQ, 18/08 : « pourquoi la liste
                       est sélectionnable ? ») : la ligne s'OUVRE, elle ne se
                       coche pas. */
                    { cle: 'code', libelle: 'Code', valeur: (f) => f.code, epinglee: true, obligatoire: true, rendu: (f) => <strong>{f.code}</strong> },
                    { cle: 'libelle', libelle: 'Examen', valeur: (f) => f.libelle, rendu: (f) => f.libelle },
                    {
                      /* PUBLIÉ ou non, en regard du nom (retour Inès) : c'est la
                         première question qu'on se pose en parcourant le
                         catalogue. Une analyse ARCHIVÉE n'est pas « non
                         publiée » — elle est retirée : les deux états se
                         distinguent, sinon on croit qu'il suffit de publier.
                         Le tri met les PUBLIÉES d'abord, c'est le catalogue
                         qu'on sert. */
                      cle: 'publie', libelle: 'Publié', valeur: (f) => (f.archivee ? 1 : 0),
                      type: 'liste', libelleValeur: (v) => (v === '1' ? 'retirée' : 'publiée'),
                      style: { whiteSpace: 'nowrap' },
                      rendu: (f) => (f.archivee
                        ? <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-md)', padding: '2px 9px', background: 'var(--gray-100)', color: 'var(--gray-700)' }}>retirée</span>
                        : <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 'var(--radius-md)', padding: '2px 9px', background: 'var(--green-50)', color: 'var(--green-700)' }}>publiée</span>),
                    },
                    {
                      /* OÙ EN EST LA FICHE (17/10/2026) : la proposition en cours,
                         si elle existe. Filtrable, pour lister « tout ce qui
                         attend ». Vide = rien en cours, et on ne l'écrit pas :
                         une colonne de 300 « — » ne dit rien. */
                      cle: 'circuit', libelle: 'En cours', valeur: (f) => circuitParCode[f.code] || '',
                      type: 'liste', libelleValeur: (v) => (v ? PASTILLE_CIRCUIT[v as StatutCircuit]?.libelle || v : 'rien en cours'),
                      style: { whiteSpace: 'nowrap' },
                      rendu: (f) => {
                        const st = circuitParCode[f.code];
                        return st ? <Pastille compacte ton={PASTILLE_CIRCUIT[st].ton} title={PASTILLE_CIRCUIT[st].aide}>{PASTILLE_CIRCUIT[st].libelle}</Pastille> : null;
                      },
                    },
                    { cle: 'famille', libelle: 'Famille', valeur: (f) => f.famille || '', type: 'liste', rendu: (f) => f.famille },
                    {
                      cle: 'analytes', libelle: 'Paramètres', filtrable: false,
                      // Le NOMBRE de paramètres : trier cette colonne sert à
                      // trouver les examens qui n'en portent aucun. Les PANELS
                      // (choix assumé) valent -1 pour se ranger APRÈS les
                      // « non lié » (travail qui reste à faire) — les
                      // confondre, c'est ce qui a laissé 34 panels invisibles
                      // jusqu'au 19/08.
                      valeur: (f) => (f.analytes.length || (String(f.nature || '').startsWith('panel') ? -1 : 0)),
                      rendu: (f) => (f.analytes.length
                        ? f.analytes.map((a) => (
                          <span key={a.documentId} style={chip} title={`${a.nom} — ${marqueVersion(a.version)}`}>
                            {a.code}
                            {/* La version du COMPOSANT, à côté du sien. Sans elle,
                                la version de l'examen ne dit pas ce qui a bougé. */}
                            <span style={{ opacity: 0.6, marginLeft: 4, fontWeight: 500 }}>{marqueVersion(a.version)}</span>
                          </span>
                        ))
                        : String(f.nature || '').startsWith('panel')
                          ? <span style={{ ...chipVide, borderStyle: 'solid' }} title={NATURES_EXAMEN.find((n) => n.code === f.nature)?.libelle}>{f.nature === 'panel_regles' ? 'panel à règles' : f.nature === 'panel_carte' ? 'panel à la carte' : 'panel'}</span>
                          : <span style={chipVide}>non lié</span>),
                    },
                    { cle: 'tech', libelle: 'Tech.', type: 'nombre', valeur: (f) => f.nbTechniques || 0, rendu: (f) => f.nbTechniques || '—' },
                    { cle: 'interv', libelle: 'Interv.', type: 'nombre', valeur: (f) => f.nbIntervallesEnVigueur || 0, rendu: (f) => f.nbIntervallesEnVigueur || '—' },
                    { cle: 'crit', libelle: 'Crit.', type: 'nombre', valeur: (f) => f.nbCriteres || 0, rendu: (f) => f.nbCriteres || '—' },
                    { cle: 'delai', libelle: 'Délai (h)', type: 'nombre', valeur: (f) => f.delaiRenduHeures ?? null, rendu: (f) => formaterDelai(f.delaiRenduHeures) },
                    {
                      cle: 'jours', libelle: 'Jours',
                      valeur: (f) => f.joursRealisation?.join(' ') || '',
                      rendu: (f) => f.joursRealisation?.join(' ') || '—',
                    },
                  ]}
                />
              </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
